import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { 
  MessageSquare, Users, Send, Image, FileText, Plus, X, Search, 
  Smile, UserPlus, Check, CheckCheck, Clock, Shield, Video, Phone,
  Maximize2, Minimize2, Mic, Play, Pause, Paperclip, MoreVertical,
  Reply, Sparkles, Volume2, Globe, Lock, ArrowLeft, Camera, User, Edit3,
  Film, Link2, ExternalLink, ChevronLeft, ChevronRight, Eye, AlertCircle, 
  VideoOff, MicOff, PhoneCall, Radio, Share2, PictureInPicture, PenTool,
  MapPin, Scale, BookOpen, Settings, UserCheck, Trash2
} from 'lucide-react';
import ElumVideoConferenceModal from './ElumVideoConferenceModal';
import ProfessionalLegalWhiteboard from './ProfessionalLegalWhiteboard';

interface ChatUser {
  id: string;
  name: string;
  email: string;
  role: string;
  phone: string;
  status: string;
  unreadCount?: number;
  lastSeen?: string;
  avatarUrl?: string;
  bio?: string;
}

interface GroupMember {
  id: string;
  groupId: string;
  userId: string;
  role: 'admin' | 'member';
  isSuspended: boolean;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
    phone?: string;
  };
}

interface Group {
  id: string;
  name: string;
  description?: string;
  avatarUrl?: string;
  createdBy: string;
  onlyAdminsCanPost: boolean;
  members: GroupMember[];
  unreadCount?: number;
}

interface Message {
  id: string;
  senderId: string;
  recipientId?: string;
  groupId?: string;
  content: string;
  type: 'text' | 'image' | 'file' | 'audio' | 'video' | 'location' | 'citation' | 'contact';
  mediaUrl?: string;
  fileName?: string;
  fileSize?: string;
  audioDuration?: string;
  locationData?: {
    latitude: number;
    longitude: number;
    title: string;
    address?: string;
  };
  citationData?: {
    journal: string;
    year: string;
    page: string;
    court?: string;
    title?: string;
  };
  contactData?: {
    name: string;
    role: string;
    phone?: string;
    email?: string;
  };
  createdAt: string;
  isRead: boolean;
  replyToId?: string;
  replyTo?: { id: string; senderName: string; content: string };
  reactions?: Record<string, number>;
  sender?: { id: string; name: string; role: string };
}

interface StatusUpdate {
  id: string;
  userId: string;
  mediaType?: 'text' | 'image' | 'video' | 'url';
  mediaUrl?: string;
  linkUrl?: string;
  targetAudience?: 'all' | 'specific';
  allowedUserIds?: string[];
  content: string;
  createdAt: string;
  expiresAt: string;
  user: { id: string; name: string; role: string; email?: string; phone?: string; avatarUrl?: string };
}

interface ChatSystemProps {
  isModalView?: boolean;
  onClose?: () => void;
}

const ChatSystem: React.FC<ChatSystemProps> = ({ isModalView = false, onClose }) => {
  const { user } = useAuth();
  const { colors, themeMode } = useTheme();
  
  // WINDOW WITHIN WINDOW CONTROLS & DYNAMIC RESIZING (Horizontal & Vertical Drag)
  const [windowState, setWindowState] = useState<'normal' | 'fullscreen' | 'minimized'>('normal');
  const [windowSize, setWindowSize] = useState<{ width: number; height: number }>({ width: 920, height: 650 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  // USER PROFILE PICTURE & BRIEF BIO STATE
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [myAvatarUrl, setMyAvatarUrl] = useState<string>(
    (user as any)?.avatarUrl || (user?.email === 'elumlawassociates@gmail.com' ? '/owner.jpeg' : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Advocate')}&background=128C7E&color=fff`)
  );
  const [myBio, setMyBio] = useState<string>(
    (user as any)?.bio || 'Senior Advocate • Constitutional Writs & Supreme Court Practice'
  );
  const [newAvatarInput, setNewAvatarInput] = useState('');
  const [newBioInput, setNewBioInput] = useState('');

  // CHAT NAVIGATION & DATA STATES
  const [contacts, setContacts] = useState<ChatUser[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [statuses, setStatuses] = useState<StatusUpdate[]>([]);
  const [activeTab, setActiveTab] = useState<'chats' | 'groups' | 'statuses'>('chats');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [selectedChat, setSelectedChat] = useState<{ type: 'direct' | 'group'; id: string; name: string; detail?: string; status?: string; avatarUrl?: string; bio?: string } | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  
  // VOICE NOTE RECORDING STATE
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);

  // QUOTED REPLY & MEDIA ATTACHMENTS
  const [replyingTo, setReplyingTo] = useState<{ id: string; senderName: string; content: string } | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{ file: File; type: 'image' | 'file' | 'video' | 'audio'; url: string } | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [activeImageModal, setActiveImageModal] = useState<string | null>(null);
  const [activeVideoModal, setActiveVideoModal] = useState<string | null>(null);

  // ALL-INCLUSIVE ATTACHMENT MODALS & PICKERS
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showCitationModal, setShowCitationModal] = useState(false);
  const [showContactShareModal, setShowContactShareModal] = useState(false);

  // Hidden File Inputs Refs for rich attachments
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const docInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const audioInputRef = useRef<HTMLInputElement | null>(null);

  // CITATION COMPOSER STATE
  const [citationJournal, setCitationJournal] = useState('SCMR');
  const [citationYear, setCitationYear] = useState('2026');
  const [citationPage, setCitationPage] = useState('');
  const [citationTitle, setCitationTitle] = useState('');
  const [citationCourt, setCitationCourt] = useState('Supreme Court of Pakistan');

  // CALL MODALS (VOICE & VIDEO - 1-ON-1 & GROUP CONFERENCES)
  const [activeCall, setActiveCall] = useState<{ type: 'voice' | 'video'; isGroup: boolean; name: string; isRinging: boolean; participants?: any[] } | null>(null);
  const [isCallMuted, setIsCallMuted] = useState(false);
  const [isCallVideoOff, setIsCallVideoOff] = useState(false);
  const [isCallScreenSharing, setIsCallScreenSharing] = useState(false);
  const [isZoomConferenceOpen, setIsZoomConferenceOpen] = useState(false);
  const [zoomMeetingTopic, setZoomMeetingTopic] = useState('');

  // MODALS FOR CREATING & MANAGING GROUPS & STATUSES
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupAvatar, setNewGroupAvatar] = useState('');
  const [newGroupOnlyAdmins, setNewGroupOnlyAdmins] = useState(false);
  const [selectedInitialMemberIds, setSelectedInitialMemberIds] = useState<string[]>([]);
  const [groupMemberSearch, setGroupMemberSearch] = useState('');

  // GROUP PROFILE & SETTINGS EDIT MODAL (CLICKABLE GROUP NAME & ICON)
  const [showGroupProfileModal, setShowGroupProfileModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [editGroupId, setEditGroupId] = useState('');
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDesc, setEditGroupDesc] = useState('');
  const [editGroupAvatar, setEditGroupAvatar] = useState('');
  const [editGroupOnlyAdmins, setEditGroupOnlyAdmins] = useState(false);
  const [isSavingGroupProfile, setIsSavingGroupProfile] = useState(false);
  const [selectedGroupForProfile, setSelectedGroupForProfile] = useState<Group | null>(null);

  // GROUP MANAGEMENT (MEMBERS, SUSPEND, ROLES, DELETE)
  const [showGroupManageModal, setShowGroupManageModal] = useState(false);
  const [selectedNewMemberId, setSelectedNewMemberId] = useState('');
  const [manageMemberSearch, setManageMemberSearch] = useState('');
  const [memberToDelete, setMemberToDelete] = useState<{ id: string; name: string } | null>(null);
  const [notifyOnDelete, setNotifyOnDelete] = useState(true);
  
  // STATUS BROADCAST & MULTI-MEDIA AUDIENCE TARGETING
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusMediaType, setStatusMediaType] = useState<'text' | 'image' | 'video' | 'url'>('text');
  const [newStatusText, setNewStatusText] = useState('');
  const [newStatusMediaUrl, setNewStatusMediaUrl] = useState('');
  const [newStatusLinkUrl, setNewStatusLinkUrl] = useState('');
  const [statusTextBg, setStatusTextBg] = useState('linear-gradient(135deg, #128c7e, #075e54)');
  const [statusTargetAudience, setStatusTargetAudience] = useState<'all' | 'specific'>('all');
  const [statusAllowedUserIds, setStatusAllowedUserIds] = useState<string[]>([]);
  const [statusAudienceSearch, setStatusAudienceSearch] = useState('');
  const [statusMediaPreview, setStatusMediaPreview] = useState<{ url: string; type: 'image' | 'video' } | null>(null);

  // STATUS VIEWER STATES
  const [activeStatusViewer, setActiveStatusViewer] = useState<StatusUpdate[] | null>(null);
  const [activeStatusIndex, setActiveStatusIndex] = useState<number>(0);
  const [statusProgress, setStatusProgress] = useState(0);
  const [isStatusPaused, setIsStatusPaused] = useState(false);

  // GROUP ADMIN VIDEO CONFERENCE RESTRICTION MODAL
  const [showGroupAdminRestrictionModal, setShowGroupAdminRestrictionModal] = useState(false);

  // DEDICATED 1-ON-1 VIDEO & AUDIO CALL STREAM REFS & PiP
  const callLocalVideoRef = useRef<HTMLVideoElement | null>(null);
  const callPipVideoRef = useRef<HTMLVideoElement | null>(null);
  const callStreamRef = useRef<MediaStream | null>(null);
  const [callDurationSeconds, setCallDurationSeconds] = useState(0);
  const [callWindowMode, setCallWindowMode] = useState<'fullscreen' | 'pip'>('fullscreen');
  const [callPipPosition, setCallPipPosition] = useState<{ x: number; y: number }>({ x: 25, y: 25 });
  const [isDraggingCallPip, setIsDraggingCallPip] = useState(false);
  const callPipDragStartRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number }>({ startX: 0, startY: 0, initialX: 0, initialY: 0 });
  const [is1on1WhiteboardOpen, setIs1on1WhiteboardOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // LISTEN FOR GLOBAL 'openLiveChat' EVENT FROM ANY NAVBAR / BADGE
  useEffect(() => {
    const handleOpenGlobalChat = () => {
      setWindowState('normal');
    };
    window.addEventListener('openLiveChat', handleOpenGlobalChat);
    return () => window.removeEventListener('openLiveChat', handleOpenGlobalChat);
  }, []);

  // MOUSE DRAG EVENT HANDLERS FOR DYNAMIC WINDOW RESIZING (HORIZONTAL & VERTICAL)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;
      const newW = Math.max(420, Math.min(window.innerWidth - 30, resizeRef.current.startW + dx));
      const newH = Math.max(380, Math.min(window.innerHeight - 30, resizeRef.current.startH + dy));
      setWindowSize({ width: newW, height: newH });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleStartResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: windowSize.width,
      startH: windowSize.height
    };
  };

  // VOICE RECORDING TIMER TICK
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      setRecordingSeconds(0);
    }
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, [isRecording]);

  // HEARTBEAT PING & INITIAL DATA FETCHING
  useEffect(() => {
    // Send immediate heartbeat on mount
    api.post('/chat/heartbeat').catch(() => {});

    fetchContacts();
    fetchGroups();
    fetchStatuses();
    
    // Heartbeat ping interval every 15s to keep active user status fresh
    const heartbeatInterval = setInterval(() => {
      api.post('/chat/heartbeat').catch(() => {});
    }, 15000);

    // Data polling interval every 3s to keep contacts, groups, and active chat in real-time sync
    const pollInterval = setInterval(() => {
      fetchContacts();
      fetchGroups();
      if (selectedChat) {
        fetchMessages(selectedChat.type, selectedChat.id);
      }
      fetchStatuses();
    }, 3000);
    
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(pollInterval);
    };
  }, [selectedChat]);

  // SCROLL TO BOTTOM ON NEW MESSAGES
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // STATUS VIEWER AUTO-PROGRESS (PAUSES ON MOUSE HOLD OR USER PAUSE)
  useEffect(() => {
    if (activeStatusViewer && !isStatusPaused) {
      setStatusProgress(0);
      progressTimerRef.current = setInterval(() => {
        setStatusProgress((prev) => {
          if (prev >= 100) {
            if (activeStatusIndex < activeStatusViewer.length - 1) {
              setActiveStatusIndex((i) => i + 1);
              return 0;
            } else {
              setActiveStatusViewer(null);
              clearInterval(progressTimerRef.current as NodeJS.Timeout);
              return 0;
            }
          }
          return prev + 2;
        });
      }, 100);
    } else {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [activeStatusViewer, activeStatusIndex, isStatusPaused]);

  // DEDICATED 1-ON-1 VIDEO & AUDIO CALL STREAM INITIALIZATION
  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: NodeJS.Timeout | null = null;

    if (activeCall && !activeCall.isGroup) {
      const startDirectCallMedia = async () => {
        try {
          if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia && activeCall.type === 'video') {
            stream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: true
            });
            callStreamRef.current = stream;
            if (callLocalVideoRef.current) {
              callLocalVideoRef.current.srcObject = stream;
            }
            if (callPipVideoRef.current) {
              callPipVideoRef.current.srcObject = stream;
            }
          }
        } catch (err) {
          console.warn('Direct 1-on-1 video call media preview note:', err);
        }
      };

      startDirectCallMedia();

      timer = setInterval(() => {
        setCallDurationSeconds(s => s + 1);
      }, 1000);
    } else {
      setCallDurationSeconds(0);
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(t => t.stop());
      }
      if (callStreamRef.current) {
        callStreamRef.current.getTracks().forEach(t => t.stop());
        callStreamRef.current = null;
      }
      if (timer) clearInterval(timer);
    };
  }, [activeCall]);

  // Synchronize local video stream when window mode or call active changes
  useEffect(() => {
    if (callStreamRef.current) {
      if (callLocalVideoRef.current) {
        callLocalVideoRef.current.srcObject = callStreamRef.current;
      }
      if (callPipVideoRef.current) {
        callPipVideoRef.current.srcObject = callStreamRef.current;
      }
    }
  }, [callWindowMode, activeCall]);

  // 1-ON-1 CALL PiP DRAG HANDLERS
  const handleCallPipMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (callWindowMode !== 'pip') return;
    setIsDraggingCallPip(true);
    callPipDragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: callPipPosition.x,
      initialY: callPipPosition.y
    };
  };

  useEffect(() => {
    const handleCallPipMouseMove = (e: MouseEvent) => {
      if (!isDraggingCallPip) return;
      const dx = e.clientX - callPipDragStartRef.current.startX;
      const dy = e.clientY - callPipDragStartRef.current.startY;
      setCallPipPosition({
        x: Math.max(10, Math.min(window.innerWidth - 380, callPipDragStartRef.current.initialX - dx)),
        y: Math.max(10, Math.min(window.innerHeight - 270, callPipDragStartRef.current.initialY - dy))
      });
    };

    const handleCallPipMouseUp = () => {
      setIsDraggingCallPip(false);
    };

    if (isDraggingCallPip) {
      window.addEventListener('mousemove', handleCallPipMouseMove);
      window.addEventListener('mouseup', handleCallPipMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleCallPipMouseMove);
      window.removeEventListener('mouseup', handleCallPipMouseUp);
    };
  }, [isDraggingCallPip]);

  const fetchContacts = async () => {
    try {
      const res = await api.get('/chat/contacts');
      if (Array.isArray(res.data)) {
        setContacts(res.data);
      }
    } catch (err) {
      console.warn('Failed to fetch chat contacts:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await api.get('/chat/groups');
      if (Array.isArray(res.data)) {
        setGroups(res.data);
      }
    } catch (err) {
      console.warn('Failed to fetch chat groups:', err);
    }
  };

  const fetchStatuses = async () => {
    try {
      const res = await api.get('/chat/status');
      if (Array.isArray(res.data)) {
        setStatuses(res.data);
      }
    } catch (err) {
      console.warn('Failed to fetch statuses:', err);
    }
  };

  const fetchMessages = async (type: 'direct' | 'group', id: string) => {
    try {
      const endpoint = type === 'direct' ? `/chat/direct/${id}` : `/chat/groups/${id}`;
      const res = await api.get(endpoint);
      if (Array.isArray(res.data)) {
        // Set messages strictly for this active chat without cross-polluting
        setMessages(res.data);
      }
    } catch (err) {
      console.warn('Error fetching messages for chat:', id);
    }
  };

  const handleSelectChat = (chat: { type: 'direct' | 'group'; id: string; name: string; detail?: string; status?: string; avatarUrl?: string; bio?: string }) => {
    setSelectedChat(chat);
    setMessages([]); // Instantly clear messages from previous chat to prevent mixing
    fetchMessages(chat.type, chat.id);
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newMessage.trim() && !attachmentPreview && !isRecording) return;
    if (!selectedChat) return;

    let msgType: Message['type'] = 'text';
    let msgMediaUrl = '';
    let msgFileName = '';

    if (attachmentPreview) {
      msgType = attachmentPreview.type;
      msgMediaUrl = attachmentPreview.url;
      msgFileName = attachmentPreview.file.name;
    }

    const payload = {
      recipientId: selectedChat.type === 'direct' ? selectedChat.id : undefined,
      groupId: selectedChat.type === 'group' ? selectedChat.id : undefined,
      content: newMessage.trim() || (attachmentPreview ? `Shared ${attachmentPreview.type}` : 'Voice Note'),
      type: msgType,
      mediaUrl: msgMediaUrl,
      fileName: msgFileName,
      replyTo: replyingTo || undefined
    };

    const tempMessage: Message = {
      id: `msg-${Date.now()}`,
      senderId: user?.id || 'me',
      recipientId: payload.recipientId,
      groupId: payload.groupId,
      content: payload.content,
      type: msgType,
      mediaUrl: msgMediaUrl,
      fileName: msgFileName,
      replyTo: payload.replyTo,
      isRead: false,
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');
    setReplyingTo(null);
    setAttachmentPreview(null);

    try {
      const endpoint = selectedChat.type === 'direct' ? '/chat/direct' : '/chat/groups/send';
      const res = await api.post(endpoint, payload);
      if (res.data && res.data.id) {
        setMessages(prev => prev.map(m => m.id === tempMessage.id ? { ...m, ...res.data } : m));
      }
    } catch (err) {
      console.warn('Sent message preserved in real-time UI.');
    }
  };

  const handleSendVoiceNote = () => {
    if (recordingSeconds === 0) return;
    setIsRecording(false);
    
    const voiceMsg: Message = {
      id: `audio-${Date.now()}`,
      senderId: user?.id || 'me',
      recipientId: selectedChat?.type === 'direct' ? selectedChat.id : undefined,
      groupId: selectedChat?.type === 'group' ? selectedChat.id : undefined,
      content: '🎙️ Legal Voice Note',
      type: 'audio',
      audioDuration: `0:${recordingSeconds < 10 ? '0' : ''}${recordingSeconds}`,
      isRead: false,
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, voiceMsg]);
  };

  const handleCreateGroup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newGroupName.trim()) {
      alert('Please provide a valid Group Name.');
      return;
    }

    try {
      const res = await api.post('/chat/groups', {
        name: newGroupName.trim(),
        description: newGroupDesc.trim(),
        avatarUrl: newGroupAvatar.trim() || undefined,
        onlyAdminsCanPost: newGroupOnlyAdmins,
        memberIds: selectedInitialMemberIds,
      });

      if (res.data && res.data.id) {
        setGroups(prev => [res.data, ...prev.filter(g => g.id !== res.data.id)]);
        fetchGroups();
        setShowGroupModal(false);
        setNewGroupName('');
        setNewGroupDesc('');
        setNewGroupAvatar('');
        setSelectedInitialMemberIds([]);
        setGroupMemberSearch('');
        setNewGroupOnlyAdmins(false);
        setActiveTab('groups');
        handleSelectChat({
          type: 'group',
          id: res.data.id,
          name: res.data.name,
          detail: res.data.description,
          avatarUrl: res.data.avatarUrl,
        });
      }
    } catch (err: any) {
      console.error('Group creation failure:', err);
      alert(err.response?.data?.message || 'Error creating group. Please verify network connectivity.');
    }
  };

  const handleAddMemberToGroup = async () => {
    if (!selectedChat || selectedChat.type !== 'group' || !selectedNewMemberId) return;
    try {
      await api.post('/chat/groups/add-member', {
        groupId: selectedChat.id,
        userId: selectedNewMemberId,
      });
      setSelectedNewMemberId('');
      fetchGroups();
      fetchMessages('group', selectedChat.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error adding member');
    }
  };

  const handleRemoveMemberFromGroup = async (userId: string, notifyMember: boolean) => {
    if (!selectedChat || selectedChat.type !== 'group') return;
    try {
      await api.post('/chat/groups/remove-member', {
        groupId: selectedChat.id,
        userId,
        notifyMember,
      });
      setMemberToDelete(null);
      fetchGroups();
      fetchMessages('group', selectedChat.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error removing member');
    }
  };

  const handleSuspendMemberInGroup = async (userId: string, isSuspended: boolean) => {
    if (!selectedChat || selectedChat.type !== 'group') return;
    try {
      await api.post('/chat/groups/suspend-member', {
        groupId: selectedChat.id,
        userId,
        isSuspended,
      });
      fetchGroups();
      fetchMessages('group', selectedChat.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating suspension');
    }
  };

  const handleSetMemberRoleInGroup = async (userId: string, role: 'admin' | 'member') => {
    if (!selectedChat || selectedChat.type !== 'group') return;
    try {
      await api.post('/chat/groups/set-role', {
        groupId: selectedChat.id,
        userId,
        role,
      });
      fetchGroups();
      fetchMessages('group', selectedChat.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating member role');
    }
  };

  const handleToggleBroadcastInGroup = async (onlyAdminsCanPost: boolean) => {
    if (!selectedChat || selectedChat.type !== 'group') return;
    try {
      await api.post('/chat/groups/toggle-broadcast', {
        groupId: selectedChat.id,
        onlyAdminsCanPost,
      });
      fetchGroups();
      fetchMessages('group', selectedChat.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating broadcast mode');
    }
  };

  // PRESET AVATARS FOR GROUPS
  const GROUP_AVATAR_PRESETS = [
    'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=150&h=150&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=150&h=150&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1453738773917-9c3eff1db985?w=150&h=150&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=150&h=150&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=150&h=150&fit=crop&crop=faces',
    'https://ui-avatars.com/api/?name=Supreme+Chambers&background=0284c7&color=fff',
    'https://ui-avatars.com/api/?name=High+Court+Appeals&background=16a34a&color=fff',
    'https://ui-avatars.com/api/?name=Corporate+Law&background=dc2626&color=fff',
  ];

  // PAKISTANI COURTS & CHAMBERS LOCATION PRESETS
  const PAK_COURTS_LOCATIONS = [
    { title: '🏛️ Supreme Court of Pakistan', address: 'Constitution Avenue, G-5/2, Islamabad', lat: 33.7294, lng: 73.0931 },
    { title: '🏛️ Lahore High Court (Principal Seat)', address: 'The Mall, Lahore, Punjab', lat: 31.5583, lng: 74.3168 },
    { title: '🏛️ High Court of Sindh (Principal Seat)', address: 'Court Road, Karachi, Sindh', lat: 24.8587, lng: 67.0205 },
    { title: '🏛️ Islamabad High Court', address: 'Sector G-10, Islamabad', lat: 33.6938, lng: 73.0652 },
    { title: '🏛️ Peshawar High Court', address: 'Khyber Road, Peshawar, KP', lat: 34.0084, lng: 71.5562 },
    { title: '🏛️ High Court of Balochistan', address: 'Hali Road, Quetta, Balochistan', lat: 30.1798, lng: 66.9750 },
    { title: '🏢 District & Sessions Courts, Islamabad', address: 'F-8 Markaz Judicial Complex, Islamabad', lat: 33.7077, lng: 73.0538 },
    { title: '🏢 District Courts / Sessions Division, Lahore', address: 'Session Court Road, Lahore', lat: 31.5723, lng: 74.3075 },
    { title: '🏢 City Courts / District Court, Karachi', address: 'M.A. Jinnah Road, Karachi', lat: 24.8615, lng: 67.0099 },
  ];

  const handleOpenGroupProfile = (group: Group) => {
    setSelectedGroupForProfile(group);
    setEditGroupId(group.id);
    setEditGroupName(group.name);
    setEditGroupDesc(group.description || '');
    setEditGroupAvatar(group.avatarUrl || '');
    setEditGroupOnlyAdmins(group.onlyAdminsCanPost || false);
    setShowGroupProfileModal(true);
  };

  const handleSaveGroupProfile = async () => {
    if (!editGroupId || !editGroupName.trim()) {
      alert('Group name cannot be empty.');
      return;
    }
    setIsSavingGroupProfile(true);
    try {
      const res = await api.post('/chat/groups/update', {
        groupId: editGroupId,
        name: editGroupName.trim(),
        description: editGroupDesc.trim(),
        avatarUrl: editGroupAvatar.trim(),
        onlyAdminsCanPost: editGroupOnlyAdmins
      });

      if (res.data && res.data.group) {
        setGroups(prev => prev.map(g => g.id === editGroupId ? { ...g, ...res.data.group } : g));
        if (selectedChat && selectedChat.id === editGroupId) {
          setSelectedChat(prev => prev ? {
            ...prev,
            name: editGroupName.trim(),
            detail: editGroupDesc.trim(),
            avatarUrl: editGroupAvatar.trim()
          } : null);
        }
        fetchGroups();
        setShowGroupProfileModal(false);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating group profile');
    } finally {
      setIsSavingGroupProfile(false);
    }
  };

  const handleRichAttachmentSelect = (type: 'image' | 'file' | 'video' | 'audio', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setAttachmentPreview({
      file,
      type,
      url: previewUrl
    });
    setShowAttachmentMenu(false);
  };

  const handleSendLocationMessage = async (title: string, lat: number, lng: number, address?: string) => {
    if (!selectedChat) return;

    const locData = {
      latitude: lat,
      longitude: lng,
      title,
      address: address || `${lat.toFixed(4)}, ${lng.toFixed(4)}`
    };

    const payload = {
      recipientId: selectedChat.type === 'direct' ? selectedChat.id : undefined,
      groupId: selectedChat.type === 'group' ? selectedChat.id : undefined,
      content: `📍 Location: ${title} (${locData.address})`,
      type: 'location',
      mediaUrl: `https://www.google.com/maps?q=${lat},${lng}`,
    };

    const tempMessage: Message = {
      id: `msg-${Date.now()}`,
      senderId: user?.id || 'me',
      recipientId: payload.recipientId,
      groupId: payload.groupId,
      content: payload.content,
      type: 'location',
      mediaUrl: payload.mediaUrl,
      locationData: locData,
      isRead: false,
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, tempMessage]);
    setShowLocationModal(false);
    setShowAttachmentMenu(false);

    try {
      const endpoint = selectedChat.type === 'direct' ? '/chat/direct' : '/chat/groups/send';
      await api.post(endpoint, payload);
    } catch (err) {
      console.warn('Location message sent in real-time UI.');
    }
  };

  const handleSendCitationMessage = async () => {
    if (!selectedChat || !citationPage.trim()) {
      alert('Please provide journal, year, and page citation details.');
      return;
    }

    const citationCode = `${citationYear} ${citationJournal} ${citationPage.trim()}`;

    const citData = {
      journal: citationJournal,
      year: citationYear,
      page: citationPage.trim(),
      court: citationCourt,
      title: citationTitle.trim()
    };

    const payload = {
      recipientId: selectedChat.type === 'direct' ? selectedChat.id : undefined,
      groupId: selectedChat.type === 'group' ? selectedChat.id : undefined,
      content: `⚖️ Precedent Citation: [${citationCode}] - ${citationCourt}${citationTitle ? ` (${citationTitle})` : ''}`,
      type: 'citation',
      mediaUrl: `/law-directory?q=${encodeURIComponent(citationCode)}`,
    };

    const tempMessage: Message = {
      id: `msg-${Date.now()}`,
      senderId: user?.id || 'me',
      recipientId: payload.recipientId,
      groupId: payload.groupId,
      content: payload.content,
      type: 'citation',
      mediaUrl: payload.mediaUrl,
      citationData: citData,
      isRead: false,
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, tempMessage]);
    setShowCitationModal(false);
    setShowAttachmentMenu(false);
    setCitationPage('');
    setCitationTitle('');

    try {
      const endpoint = selectedChat.type === 'direct' ? '/chat/direct' : '/chat/groups/send';
      await api.post(endpoint, payload);
    } catch (err) {
      console.warn('Citation message sent in real-time UI.');
    }
  };

  const handleSendContactMessage = async (contact: ChatUser) => {
    if (!selectedChat) return;

    const contData = {
      name: contact.name,
      role: contact.role?.replace('_', ' ') || 'Advocate',
      phone: contact.phone || 'N/A',
      email: contact.email || 'N/A'
    };

    const payload = {
      recipientId: selectedChat.type === 'direct' ? selectedChat.id : undefined,
      groupId: selectedChat.type === 'group' ? selectedChat.id : undefined,
      content: `📇 Advocate Contact Card: ${contact.name} (${contData.role}) • Phone: ${contData.phone}`,
      type: 'contact',
    };

    const tempMessage: Message = {
      id: `msg-${Date.now()}`,
      senderId: user?.id || 'me',
      recipientId: payload.recipientId,
      groupId: payload.groupId,
      content: payload.content,
      type: 'contact',
      contactData: contData,
      isRead: false,
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, tempMessage]);
    setShowContactShareModal(false);
    setShowAttachmentMenu(false);

    try {
      const endpoint = selectedChat.type === 'direct' ? '/chat/direct' : '/chat/groups/send';
      await api.post(endpoint, payload);
    } catch (err) {
      console.warn('Contact message sent in real-time UI.');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImg = file.type.startsWith('image/');
    const isVid = file.type.startsWith('video/');
    const isAud = file.type.startsWith('audio/');
    const url = URL.createObjectURL(file);
    setAttachmentPreview({ 
      file, 
      type: isImg ? 'image' : isVid ? 'video' : isAud ? 'audio' : 'file', 
      url 
    });
  };

  const handleProfilePictureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setNewAvatarInput(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleGroupAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 256;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
          setNewGroupAvatar(compressedDataUrl);
        }
      };
      if (typeof event.target?.result === 'string') {
        img.src = event.target.result;
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = () => {
    if (newAvatarInput.trim()) {
      setMyAvatarUrl(newAvatarInput.trim());
    }
    if (newBioInput.trim()) {
      setMyBio(newBioInput.trim());
    }
    setShowProfileModal(false);
  };

  // PALETTE BASED ON ACTIVE THEME
  const chatBg = themeMode === 'light' ? '#efeae2' : themeMode === 'easy' ? '#e5dec9' : '#0b141a';
  const sidebarBg = themeMode === 'light' ? '#ffffff' : themeMode === 'easy' ? '#fbf7ee' : '#111b21';
  const headerBg = themeMode === 'light' ? '#f0f2f5' : themeMode === 'easy' ? '#ebdcc1' : '#202c33';
  const myBubbleBg = themeMode === 'light' ? '#d9fdd3' : themeMode === 'easy' ? '#d1e7dd' : '#005c4b';
  const otherBubbleBg = themeMode === 'light' ? '#ffffff' : themeMode === 'easy' ? '#fbf7ee' : '#202c33';
  const textColor = themeMode === 'light' ? '#111b21' : themeMode === 'easy' ? '#1c2d28' : '#e9edef';

  // IF MINIMIZED -> DOCK AS FLOATING BADGE BUTTON AT BOTTOM-RIGHT
  if (windowState === 'minimized') {
    return (
      <div 
        onClick={() => setWindowState('normal')}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 99999,
          backgroundColor: '#128c7e',
          color: 'white',
          padding: '0.85rem 1.35rem',
          borderRadius: '50px',
          cursor: 'pointer',
          boxShadow: '0 10px 25px rgba(18, 140, 126, 0.5), 0 4px 10px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          fontWeight: '800',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <MessageSquare size={22} />
        <span>🟢 Elum Live Chat Node</span>
        <span style={{ backgroundColor: 'white', color: '#128c7e', padding: '0.15rem 0.55rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '900' }}>
          3
        </span>
      </div>
    );
  }

  return (
    <div style={{
      position: isModalView ? 'fixed' : 'relative',
      top: isModalView ? (windowState === 'fullscreen' ? 0 : 'auto') : 'auto',
      left: isModalView ? (windowState === 'fullscreen' ? 0 : 'auto') : 'auto',
      right: isModalView ? (windowState === 'fullscreen' ? 0 : '24px') : 'auto',
      bottom: isModalView ? (windowState === 'fullscreen' ? 0 : '24px') : 'auto',
      width: isModalView ? (windowState === 'fullscreen' ? '100vw' : `${windowSize.width}px`) : '100%',
      height: isModalView ? (windowState === 'fullscreen' ? '100vh' : `${windowSize.height}px`) : '750px',
      maxWidth: isModalView ? (windowState === 'fullscreen' ? '100vw' : '95vw') : '100%',
      maxHeight: isModalView ? (windowState === 'fullscreen' ? '100vh' : '90vh') : '100%',
      backgroundColor: sidebarBg,
      color: textColor,
      borderRadius: windowState === 'fullscreen' ? '0' : '16px',
      border: `2px solid ${colors.goldPrimary}`,
      boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 30px rgba(18,140,126,0.25)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: isModalView ? 9999 : 1,
      fontFamily: '"Outfit", "Segoe UI", sans-serif',
      transition: isResizing ? 'none' : 'width 0.15s ease, height 0.15s ease'
    }}>

      {/* ELUM LIVE CHAT TITLE BAR & WINDOW CONTROLS */}
      <div style={{
        backgroundColor: '#128c7e',
        color: 'white',
        padding: '0.65rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `2px solid ${colors.goldPrimary}`,
        userSelect: 'none'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <div style={{ backgroundColor: '#00a884', padding: '0.35rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageSquare size={18} color="white" />
          </div>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: '900', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              Elum Live Chat <span style={{ fontSize: '0.7rem', backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.1rem 0.4rem', borderRadius: '10px' }}>Encrypted 🔒</span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#bbf7d0' }}>[Judicial Officers Secure & Encrypted Network]</div>
          </div>
        </div>

        {/* WINDOW CONTROLS (_ [ ] X) AT EXTREME TOP RIGHT */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
          <button 
            onClick={() => setWindowState('minimized')}
            title="Minimize Chat Window"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
          >
            _
          </button>
          <button 
            onClick={() => setWindowState(windowState === 'fullscreen' ? 'normal' : 'fullscreen')}
            title={windowState === 'fullscreen' ? "Restore Window Size" : "Maximize Fullscreen"}
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            {windowState === 'fullscreen' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          {onClose && (
            <button 
              onClick={onClose}
              title="Close Chat Window"
              style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontWeight: 'bold' }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* DUAL PANE LAYOUT (SIDEBAR + ACTIVE CHAT PANEL) */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* LEFT SIDEBAR (CONTACTS, GROUPS, STATUSES) */}
        <div style={{
          width: '320px',
          minWidth: '280px',
          backgroundColor: sidebarBg,
          borderRight: `1px solid ${colors.borderThemeColor}`,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* USER PROFILE CARD WITH EDIT PICTURE & BIO OPTION */}
          <div style={{ backgroundColor: headerBg, padding: '0.85rem 1rem', borderBottom: `1px solid ${colors.borderThemeColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div 
                onClick={() => { setNewAvatarInput(myAvatarUrl); setNewBioInput(myBio); setShowProfileModal(true); }}
                title="Click to Update Your Profile Picture & Bio Description"
                style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', cursor: 'pointer' }}
              >
                <div style={{ position: 'relative' }}>
                  <img src={myAvatarUrl} alt="Avatar" style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${colors.goldPrimary}` }} />
                  <div style={{ position: 'absolute', bottom: 0, right: 0, backgroundColor: colors.goldPrimary, color: 'black', borderRadius: '50%', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Camera size={10} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.88rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    {user?.name || 'Advocate User'} <Edit3 size={12} color={colors.goldPrimary} />
                  </div>
                  <div style={{ fontSize: '0.72rem', color: colors.goldPrimary, textTransform: 'uppercase', fontWeight: '700' }}>
                    {user?.role?.replace('_', ' ') || 'Practitioner'}
                  </div>
                </div>
              </div>
              <button onClick={() => setShowGroupModal(true)} title="Create New Practice Group Channel" style={{ backgroundColor: 'transparent', border: 'none', color: colors.goldPrimary, cursor: 'pointer', padding: '0.3rem' }}>
                <Plus size={20} />
              </button>
            </div>

            {/* CHAT NAVIGATION TABS */}
            <div style={{ display: 'flex', backgroundColor: sidebarBg, borderRadius: '8px', padding: '0.2rem', gap: '0.2rem' }}>
              <button 
                onClick={() => setActiveTab('chats')}
                style={{ flex: 1, padding: '0.45rem', borderRadius: '6px', border: 'none', backgroundColor: activeTab === 'chats' ? '#128c7e' : 'transparent', color: activeTab === 'chats' ? 'white' : textColor, fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}
              >
                💬 Chats
              </button>
              <button 
                onClick={() => setActiveTab('groups')}
                style={{ flex: 1, padding: '0.45rem', borderRadius: '6px', border: 'none', backgroundColor: activeTab === 'groups' ? '#128c7e' : 'transparent', color: activeTab === 'groups' ? 'white' : textColor, fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}
              >
                👥 Groups ({groups.length})
              </button>
              <button 
                onClick={() => setActiveTab('statuses')}
                style={{ flex: 1, padding: '0.45rem', borderRadius: '6px', border: 'none', backgroundColor: activeTab === 'statuses' ? '#128c7e' : 'transparent', color: activeTab === 'statuses' ? 'white' : textColor, fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}
              >
                ⭕ Status
              </button>
            </div>
          </div>

          {/* SEARCH BAR */}
          <div style={{ padding: '0.65rem 1rem', backgroundColor: sidebarBg, borderBottom: `1px solid ${colors.borderThemeColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: headerBg, borderRadius: '8px', padding: '0.45rem 0.75rem', gap: '0.5rem' }}>
              <Search size={16} color="#8696a0" />
              <input 
                type="text" 
                placeholder="Search advocates, groups or messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ backgroundColor: 'transparent', border: 'none', color: textColor, width: '100%', outline: 'none', fontSize: '0.82rem' }}
              />
            </div>
          </div>

          {/* LIST ITEMS (CHATS, GROUPS, STATUSES) */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {activeTab === 'chats' && (() => {
              const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()) || (g.description && g.description.toLowerCase().includes(searchQuery.toLowerCase())));
              const filteredContacts = contacts.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.email.toLowerCase().includes(searchQuery.toLowerCase()));

              return (
                <div>
                  {/* GROUPS SECTION IN MAIN CHAT LIST */}
                  {filteredGroups.length > 0 && (
                    <div style={{ borderBottom: `1px solid ${colors.borderThemeColor}` }}>
                      <div style={{ padding: '0.45rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: headerBg }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: '800', color: colors.goldPrimary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          👥 Practice Groups & Bar Councils ({filteredGroups.length})
                        </span>
                        <button 
                          onClick={() => setShowGroupModal(true)} 
                          style={{ fontSize: '0.68rem', padding: '0.15rem 0.5rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          + New Group
                        </button>
                      </div>

                      {filteredGroups.map((group) => {
                        const isSelected = selectedChat?.id === group.id;
                        const memberCount = group.members?.length || 0;
                        const isCreator = group.createdBy === user?.id;

                        return (
                          <div 
                            key={group.id}
                            onClick={() => handleSelectChat({ type: 'group', id: group.id, name: group.name, detail: group.description, avatarUrl: group.avatarUrl })}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.85rem',
                              padding: '0.85rem 1rem',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? headerBg : 'transparent',
                              borderBottom: `1px solid ${colors.borderThemeColor}`,
                              transition: 'background-color 0.15s'
                            }}
                          >
                            <div style={{ width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#00a884', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '1.2rem', overflow: 'hidden', flexShrink: 0 }}>
                              {group.avatarUrl ? (
                                <img src={group.avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                '👥'
                              )}
                            </div>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: '800', fontSize: '0.88rem', color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{group.name}</span>
                                {isCreator && <span style={{ fontSize: '0.65rem', backgroundColor: colors.goldPrimary, color: 'black', padding: '0.1rem 0.35rem', borderRadius: '4px', fontWeight: '800' }}>Admin</span>}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#8696a0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {group.onlyAdminsCanPost ? '📢 Broadcast Mode • ' : ''}{memberCount} Members • {group.description || 'Chambers Channel'}
                              </div>
                            </div>
                            {group.unreadCount && group.unreadCount > 0 ? (
                              <span style={{ backgroundColor: '#25d366', color: 'white', borderRadius: '50%', fontSize: '0.7rem', fontWeight: '900', padding: '0.2rem 0.5rem' }}>
                                {group.unreadCount}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* DIRECT CONTACTS SECTION IN MAIN CHAT LIST */}
                  <div>
                    <div style={{ padding: '0.45rem 1rem', backgroundColor: headerBg, borderBottom: `1px solid ${colors.borderThemeColor}` }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: '800', color: colors.goldPrimary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        💬 Direct Advocate Chats ({filteredContacts.length})
                      </span>
                    </div>

                    {filteredContacts.map((contact) => {
                      const isSelected = selectedChat?.id === contact.id;
                      const contactAvatar = contact.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&background=128C7E&color=fff`;
                      return (
                        <div 
                          key={contact.id}
                          onClick={() => handleSelectChat({ type: 'direct', id: contact.id, name: contact.name, detail: contact.role, status: contact.status, avatarUrl: contactAvatar, bio: contact.bio })}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.85rem',
                            padding: '0.85rem 1rem',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? headerBg : 'transparent',
                            borderBottom: `1px solid ${colors.borderThemeColor}`,
                            transition: 'background-color 0.15s'
                          }}
                        >
                          <div style={{ position: 'relative' }}>
                            <img src={contactAvatar} alt="Avatar" style={{ width: '45px', height: '45px', borderRadius: '50%', objectFit: 'cover' }} />
                            <span style={{ position: 'absolute', bottom: '2px', right: '2px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: contact.status === 'online' ? '#25d366' : '#94a3b8', border: '2px solid white' }}></span>
                          </div>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: '800', fontSize: '0.9rem', color: textColor, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{contact.name}</span>
                              <span style={{ fontSize: '0.7rem', color: contact.status === 'online' ? '#25d366' : '#8696a0', fontWeight: contact.status === 'online' ? '700' : 'normal' }}>
                                {contact.status === 'online' ? 'Online' : (contact.lastSeen || 'Offline')}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: colors.goldPrimary, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {contact.role.replace('_', ' ')} • {contact.phone}
                            </div>
                          </div>
                          {contact.unreadCount && contact.unreadCount > 0 ? (
                            <span style={{ backgroundColor: '#25d366', color: 'white', borderRadius: '50%', fontSize: '0.7rem', fontWeight: '900', padding: '0.2rem 0.5rem' }}>
                              {contact.unreadCount}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {activeTab === 'groups' && (
              <div style={{ padding: '0.5rem 0' }}>
                <div style={{ padding: '0.5rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: '800', color: colors.goldPrimary, textTransform: 'uppercase' }}>Practice Groups & Bar Councils</span>
                  <button 
                    onClick={() => setShowGroupModal(true)} 
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    + New Group
                  </button>
                </div>

                {groups.length === 0 ? (
                  <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#8696a0', fontSize: '0.82rem' }}>
                    No groups found. Click <b>+ New Group</b> above to create a Bar Council or Practice Group channel.
                  </div>
                ) : (
                  groups
                    .filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((group) => {
                      const isSelected = selectedChat?.id === group.id;
                      const memberCount = group.members?.length || 0;
                      const isCreator = group.createdBy === user?.id;
                      return (
                        <div 
                          key={group.id}
                          onClick={() => handleSelectChat({ type: 'group', id: group.id, name: group.name, detail: group.description, avatarUrl: group.avatarUrl })}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.85rem',
                            padding: '0.85rem 1rem',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? headerBg : 'transparent',
                            borderBottom: `1px solid ${colors.borderThemeColor}`,
                            transition: 'background-color 0.15s'
                          }}
                        >
                          <div style={{ width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#00a884', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '1.2rem', overflow: 'hidden', flexShrink: 0 }}>
                            {group.avatarUrl ? (
                              <img src={group.avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              '👥'
                            )}
                          </div>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: '800', fontSize: '0.88rem', color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{group.name}</span>
                              {isCreator && <span style={{ fontSize: '0.65rem', backgroundColor: colors.goldPrimary, color: 'black', padding: '0.1rem 0.35rem', borderRadius: '4px', fontWeight: '800' }}>Admin</span>}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#8696a0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {group.onlyAdminsCanPost ? '📢 Broadcast Mode • ' : ''}{memberCount} Members • {group.description || 'Chambers Channel'}
                            </div>
                          </div>
                          {group.unreadCount && group.unreadCount > 0 ? (
                            <span style={{ backgroundColor: '#25d366', color: 'white', borderRadius: '50%', fontSize: '0.7rem', fontWeight: '900', padding: '0.2rem 0.5rem' }}>
                              {group.unreadCount}
                            </span>
                          ) : null}
                        </div>
                      );
                    })
                )}
              </div>
            )}

            {activeTab === 'statuses' && (
              <div style={{ padding: '1rem' }}>
                {/* MY STATUS CARD */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: headerBg, borderRadius: '12px', marginBottom: '1.25rem', border: `1px solid ${colors.borderThemeColor}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ position: 'relative' }}>
                      <img src={myAvatarUrl} alt="My Status" style={{ width: '46px', height: '46px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${colors.goldPrimary}` }} />
                      <button 
                        onClick={() => {
                          setStatusMediaType('text');
                          setNewStatusText('');
                          setNewStatusMediaUrl('');
                          setNewStatusLinkUrl('');
                          setStatusTargetAudience('all');
                          setStatusAllowedUserIds([]);
                          setShowStatusModal(true);
                        }}
                        title="Create New Status Dispatch"
                        style={{ position: 'absolute', bottom: -2, right: -2, backgroundColor: '#25d366', color: 'white', border: '2px solid black', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                    <div>
                      <div style={{ fontWeight: '800', fontSize: '0.88rem' }}>My Legal Dispatch</div>
                      <div style={{ fontSize: '0.72rem', color: '#8696a0' }}>Click + to broadcast image, video, URL or text</div>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => {
                    setStatusMediaType('text');
                    setNewStatusText('');
                    setNewStatusMediaUrl('');
                    setNewStatusLinkUrl('');
                    setStatusTargetAudience('all');
                    setStatusAllowedUserIds([]);
                    setShowStatusModal(true);
                  }} 
                  style={{ width: '100%', padding: '0.75rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', boxShadow: '0 4px 12px rgba(18, 140, 126, 0.3)' }}
                >
                  <Plus size={18} /> Post 24h Legal Dispatch
                </button>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: '800', color: colors.goldPrimary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Recent Status Dispatches ({statuses.length})
                  </span>
                </div>

                {statuses.length === 0 ? (
                  <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#8696a0', fontSize: '0.82rem' }}>
                    No active dispatches. Broadcast case precedents, urgent hearing notices, or legal updates to the Bar & Bench.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    {statuses.map((st) => {
                      const mType = st.mediaType || (st.mediaUrl?.match(/\.(mp4|webm|mov)$/i) ? 'video' : st.mediaUrl ? 'image' : 'text');
                      return (
                        <div 
                          key={st.id}
                          onClick={() => { setActiveStatusViewer([st]); setActiveStatusIndex(0); }}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 0.75rem', cursor: 'pointer', backgroundColor: headerBg, borderRadius: '10px', border: `1px solid ${colors.borderThemeColor}`, transition: 'all 0.15s' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ width: '45px', height: '45px', borderRadius: '50%', border: '2.5px solid #25d366', padding: '2px', position: 'relative', flexShrink: 0 }}>
                              {mType === 'image' && st.mediaUrl ? (
                                <img src={st.mediaUrl} alt="Status" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                              ) : mType === 'video' ? (
                                <div style={{ width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                  <Film size={18} />
                                </div>
                              ) : mType === 'url' ? (
                                <div style={{ width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                  <Link2 size={18} />
                                </div>
                              ) : (
                                <div style={{ width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#0f766e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                  ✍️
                                </div>
                              )}
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                              <div style={{ fontWeight: '800', fontSize: '0.85rem', color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {st.user?.name || 'Advocate'}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#8696a0', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                <span>{new Date(st.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                <span>•</span>
                                <span style={{ color: mType === 'video' ? '#38bdf8' : mType === 'image' ? '#4ade80' : mType === 'url' ? '#c084fc' : '#fef08a', fontWeight: '700' }}>
                                  {mType === 'video' ? '🎥 Video' : mType === 'image' ? '📸 Photo' : mType === 'url' ? '🔗 URL Link' : '✍️ Text'}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div style={{ flexShrink: 0 }}>
                            {st.targetAudience === 'specific' ? (
                              <span title="Shared with Specific Contacts Only" style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#eab308', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '800' }}>
                                🔒 Specific
                              </span>
                            ) : (
                              <span title="Shared with All Users" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '800' }}>
                                🌐 All
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT ACTIVE CHAT PANEL */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: chatBg, position: 'relative' }}>
          {selectedChat ? (
            <>
              {/* ACTIVE CHAT HEADER */}
              {(() => {
                const selectedContact = contacts.find(c => c.id === selectedChat.id);
                const isSelectedContactOnline = selectedChat.type === 'direct'
                  ? (selectedContact ? selectedContact.status === 'online' : selectedChat.status === 'online')
                  : true;
                const lastSeenText = selectedContact?.lastSeen || 'Offline';
                const activeBio = selectedContact?.bio || selectedChat.bio || 'Advocate Legal Practitioner';
                const currentGroup = selectedChat.type === 'group' ? groups.find(g => g.id === selectedChat.id) : null;

                return (
                  <div style={{
                    backgroundColor: headerBg,
                    padding: '0.75rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${colors.borderThemeColor}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <button 
                        onClick={() => setSelectedChat(null)}
                        title="Back to All Conversations"
                        style={{ backgroundColor: 'rgba(0,0,0,0.06)', color: textColor, border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <ArrowLeft size={18} />
                      </button>

                      {/* CLICKABLE AVATAR & NAME (FOR BOTH INDIVIDUALS & GROUPS) */}
                      <div 
                        onClick={() => {
                          if (selectedChat.type === 'group' && currentGroup) {
                            handleOpenGroupProfile(currentGroup);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          cursor: selectedChat.type === 'group' ? 'pointer' : 'default',
                          padding: '0.2rem 0.35rem',
                          borderRadius: '8px',
                          transition: 'background-color 0.2s'
                        }}
                        title={selectedChat.type === 'group' ? "Click to View & Edit Group Profile, Description & Icon" : undefined}
                      >
                        <div style={{ position: 'relative' }}>
                          <img 
                            src={selectedChat.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedChat.name)}&background=128C7E&color=fff`} 
                            alt="Avatar" 
                            style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', border: `1.5px solid ${colors.goldPrimary}` }} 
                          />
                          {selectedChat.type === 'group' && (
                            <span style={{ position: 'absolute', bottom: '-2px', right: '-2px', backgroundColor: colors.goldPrimary, color: 'black', borderRadius: '50%', width: '15px', height: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', border: '1px solid black' }}>
                              <Edit3 size={9} />
                            </span>
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: '900', fontSize: '0.95rem', color: textColor, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span>{selectedChat.name}</span>
                            {/* Broadcast Mode badge is hidden as requested */}
                            {selectedChat.type === 'group' && (
                              <span style={{ fontSize: '0.68rem', color: colors.goldPrimary, opacity: 0.85 }}>
                                <Edit3 size={12} />
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: isSelectedContactOnline ? '#25d366' : '#94a3b8', fontWeight: '700' }}>
                            {selectedChat.type === 'group' 
                              ? `${currentGroup?.members?.length || 0} Members • ${selectedChat.detail || currentGroup?.description || 'Click to edit group'}`
                              : isSelectedContactOnline 
                                ? `🟢 Online • ${activeBio}` 
                                : `⚪ Offline • ${lastSeenText}`}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* CALL BUTTONS (VOICE & VIDEO) & GROUP ACTION BADGES (+ SIGN & SETTINGS) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {selectedChat.type === 'group' && currentGroup && (
                        <>
                          {/* + Sign Badge to Add Members */}
                          <button 
                            onClick={() => { 
                              fetchGroups(); 
                              setSelectedGroupForProfile(currentGroup);
                              setShowAddMemberModal(true); 
                            }}
                            title="Add Advocate / Member to this Group (+)"
                            style={{ 
                              width: '38px', 
                              height: '38px', 
                              borderRadius: '50%', 
                              backgroundColor: '#128c7e', 
                              color: 'white', 
                              border: 'none', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              boxShadow: '0 2px 8px rgba(18,140,126,0.35)',
                              transition: 'transform 0.15s'
                            }}
                          >
                            <Plus size={20} />
                          </button>

                          {/* Settings Symbol Badge for Group Settings, Roles & Members */}
                          <button 
                            onClick={() => { 
                              fetchGroups(); 
                              handleOpenGroupProfile(currentGroup); 
                            }}
                            title={`Group Settings & Roster (${currentGroup.members?.length || 1} Members)`}
                            style={{ 
                              width: '38px', 
                              height: '38px', 
                              borderRadius: '50%', 
                              backgroundColor: 'rgba(255,255,255,0.08)', 
                              color: colors.goldPrimary, 
                              border: `1.5px solid ${colors.goldPrimary}`, 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              position: 'relative' 
                            }}
                          >
                            <Settings size={18} />
                            <span style={{ position: 'absolute', top: '-4px', right: '-4px', backgroundColor: '#0284c7', color: 'white', fontSize: '0.62rem', fontWeight: '900', padding: '0.05rem 0.35rem', borderRadius: '10px', border: '1.5px solid #0f172a' }}>
                              {currentGroup.members?.length || 1}
                            </span>
                          </button>
                        </>
                      )}

                      <button 
                        onClick={() => setActiveCall({ 
                          type: 'voice', 
                          isGroup: selectedChat.type === 'group', 
                          name: selectedChat.name, 
                          isRinging: true, 
                          participants: currentGroup?.members || [] 
                        })}
                        title={selectedChat.type === 'group' ? "Start Group Conference Call" : "Start Voice Call"}
                        style={{ backgroundColor: 'rgba(37,211,102,0.15)', color: '#25d366', border: '1px solid #25d366', borderRadius: '50%', width: '38px', height: '38px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Phone size={18} />
                      </button>
                      <button 
                        onClick={() => {
                          if (selectedChat.type === 'group') {
                            const myMember = currentGroup?.members?.find(m => m.userId === user?.id);
                            const isGroupAdmin = Boolean(
                              !currentGroup?.createdBy || 
                              currentGroup?.createdBy === user?.id || 
                              myMember?.role === 'admin' || 
                              user?.role === 'admin' || 
                              user?.role === 'superuser' || 
                              user?.email === 'elumlawassociates@gmail.com'
                            );

                            if (!isGroupAdmin) {
                              setShowGroupAdminRestrictionModal(true);
                              return;
                            }

                            setZoomMeetingTopic(`🏛️ ${selectedChat.name} — Judicial Group Conference`);
                            setIsZoomConferenceOpen(true);
                          } else {
                            // Dedicated 1-on-1 Direct Video Call
                            setActiveCall({
                              type: 'video',
                              isGroup: false,
                              name: selectedChat.name,
                              isRinging: true
                            });
                          }
                        }}
                        title={selectedChat.type === 'group' ? "Start Group Video Conference (Elum Meet • Admin Permission)" : "Start Dedicated 1-on-1 HD Video Call"}
                        style={{ backgroundColor: 'rgba(37,211,102,0.15)', color: '#25d366', border: '1px solid #25d366', borderRadius: '50%', width: '38px', height: '38px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Video size={18} />
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* MESSAGES SCROLL AREA */}
              <div style={{ flex: 1, padding: '1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div style={{ textAlign: 'center', margin: '0.5rem 0' }}>
                  <span style={{ backgroundColor: headerBg, color: '#8696a0', fontSize: '0.72rem', padding: '0.3rem 0.85rem', borderRadius: '12px', fontWeight: '800' }}>
                    🔒 Bar & Bench on a page with end to end encryption
                  </span>
                </div>

                {(() => {
                  const selectedContact = contacts.find(c => c.id === selectedChat.id);
                  const isSelectedContactOnline = selectedChat.type === 'direct'
                    ? (selectedContact ? selectedContact.status === 'online' : selectedChat.status === 'online')
                    : true;

                  const myId = user?.id || 'me';
                  const activeMessages = messages.filter((msg) => {
                    if (selectedChat.type === 'group') {
                      return msg.groupId === selectedChat.id;
                    } else {
                      return (
                        (msg.senderId === myId && msg.recipientId === selectedChat.id) ||
                        (msg.senderId === selectedChat.id && (msg.recipientId === myId || !msg.recipientId)) ||
                        (!msg.groupId && (msg.recipientId === selectedChat.id || msg.senderId === selectedChat.id))
                      );
                    }
                  });

                  return activeMessages.map((msg) => {
                    const isMe = msg.senderId === (user?.id || 'me');
                    return (
                      <div 
                        key={msg.id}
                        style={{
                          alignSelf: isMe ? 'flex-end' : 'flex-start',
                          maxWidth: '75%',
                          backgroundColor: isMe ? myBubbleBg : otherBubbleBg,
                          color: isMe && themeMode === 'dark' ? '#e9edef' : textColor,
                          borderRadius: isMe ? '12px 0 12px 12px' : '0 12px 12px 12px',
                          padding: '0.65rem 0.95rem',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                          position: 'relative'
                        }}
                      >
                        {/* SENDER NAME IF IN GROUP */}
                        {!isMe && selectedChat.type === 'group' && (
                          <div style={{ fontSize: '0.75rem', fontWeight: '900', color: colors.goldPrimary, marginBottom: '0.2rem' }}>
                            {msg.sender?.name || 'Advocate'}
                          </div>
                        )}

                        {/* QUOTED REPLY PREVIEW IF ANY */}
                        {msg.replyTo && (
                          <div style={{ backgroundColor: 'rgba(0,0,0,0.15)', borderLeft: `3px solid ${colors.goldPrimary}`, padding: '0.3rem 0.6rem', borderRadius: '4px', marginBottom: '0.45rem', fontSize: '0.78rem' }}>
                            <div style={{ fontWeight: '800', color: colors.goldPrimary }}>{msg.replyTo.senderName}</div>
                            <div style={{ color: '#8696a0' }}>{msg.replyTo.content}</div>
                          </div>
                        )}

                        {/* AUDIO VOICE NOTE BUBBLE */}
                        {msg.type === 'audio' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: '220px', padding: '0.25rem 0' }}>
                            <button 
                              onClick={() => setPlayingAudioId(playingAudioId === msg.id ? null : msg.id)}
                              style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#25d366', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              {playingAudioId === msg.id ? <Pause size={18} /> : <Play size={18} />}
                            </button>
                            <div style={{ flex: 1 }}>
                              <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{ width: playingAudioId === msg.id ? '65%' : '0%', height: '100%', backgroundColor: '#25d366', transition: 'width 0.3s' }}></div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#8696a0', marginTop: '0.3rem' }}>
                                <span>🎙️ Voice Note</span>
                                <span>{msg.audioDuration || '0:15'}</span>
                              </div>
                            </div>
                          </div>
                        ) : msg.type === 'location' ? (
                          /* GPS & COURT LOCATION CARD */
                          <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', borderRadius: '10px', padding: '0.65rem 0.85rem', border: '1px solid rgba(56, 189, 248, 0.4)', minWidth: '240px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#38bdf8', fontWeight: '800', fontSize: '0.88rem', marginBottom: '0.25rem' }}>
                              <MapPin size={16} />
                              <span>{msg.locationData?.title || 'Court & Chambers Location'}</span>
                            </div>
                            {msg.locationData?.address && (
                              <div style={{ fontSize: '0.75rem', color: '#cbd5e1', marginBottom: '0.45rem', lineHeight: '1.4' }}>
                                {msg.locationData.address}
                              </div>
                            )}
                            <a 
                              href={msg.mediaUrl || `https://www.google.com/maps?q=${msg.locationData?.latitude || 33.7294},${msg.locationData?.longitude || 73.0931}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', backgroundColor: '#0284c7', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800', textDecoration: 'none' }}
                            >
                              <ExternalLink size={13} /> Open in Google Maps
                            </a>
                          </div>
                        ) : msg.type === 'citation' ? (
                          /* JUDICIAL CITATION PRECEDENT CARD */
                          <div style={{ backgroundColor: 'rgba(234, 179, 8, 0.12)', borderRadius: '10px', padding: '0.65rem 0.85rem', border: `1.5px solid ${colors.goldPrimary}`, minWidth: '240px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.goldPrimary, fontWeight: '900', fontSize: '0.9rem', marginBottom: '0.2rem' }}>
                              <Scale size={16} />
                              <span>{msg.citationData?.year} {msg.citationData?.journal} {msg.citationData?.page}</span>
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: 'bold' }}>
                              {msg.citationData?.court || 'Supreme Court of Pakistan'}
                            </div>
                            {msg.citationData?.title && (
                              <div style={{ fontSize: '0.75rem', color: '#cbd5e1', marginTop: '0.25rem' }}>
                                {msg.citationData.title}
                              </div>
                            )}
                            <div style={{ marginTop: '0.4rem', fontSize: '0.68rem', color: colors.goldPrimary, fontWeight: 'bold' }}>
                              ⚖️ Verified Supreme & High Court Precedent
                            </div>
                          </div>
                        ) : msg.type === 'contact' ? (
                          /* ADVOCATE CONTACT CARD */
                          <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.12)', borderRadius: '10px', padding: '0.65rem 0.85rem', border: '1px solid #22c55e', minWidth: '230px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#22c55e', fontWeight: '800', fontSize: '0.88rem', marginBottom: '0.2rem' }}>
                              <UserCheck size={16} />
                              <span>{msg.contactData?.name || 'Advocate Contact'}</span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#e2e8f0', fontWeight: 'bold' }}>
                              {msg.contactData?.role || 'Legal Practitioner'}
                            </div>
                            {msg.contactData?.phone && msg.contactData.phone !== 'N/A' && (
                              <div style={{ fontSize: '0.75rem', color: '#cbd5e1', marginTop: '0.2rem' }}>
                                📞 {msg.contactData.phone}
                              </div>
                            )}
                            {msg.contactData?.email && msg.contactData.email !== 'N/A' && (
                              <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
                                ✉️ {msg.contactData.email}
                              </div>
                            )}
                          </div>
                        ) : msg.type === 'file' ? (
                          /* LEGAL DOCUMENT CARD */
                          <div style={{ backgroundColor: 'rgba(0,0,0,0.25)', padding: '0.6rem 0.85rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.65rem', border: '1px solid rgba(255,255,255,0.15)', minWidth: '220px' }}>
                            <FileText size={24} color="#38bdf8" />
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{ fontWeight: 'bold', fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {msg.fileName || msg.content || 'Legal Document.pdf'}
                              </div>
                              <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Legal Pleadings / Case File</div>
                            </div>
                            {msg.mediaUrl && (
                              <a href={msg.mediaUrl} download target="_blank" rel="noopener noreferrer" style={{ color: colors.goldPrimary, textDecoration: 'none', padding: '0.2rem' }}>
                                <ExternalLink size={16} />
                              </a>
                            )}
                          </div>
                        ) : msg.type === 'video' && msg.mediaUrl ? (
                          /* VIDEO EVIDENCE CLIP */
                          <div style={{ borderRadius: '8px', overflow: 'hidden', minWidth: '240px' }}>
                            <video 
                              src={msg.mediaUrl} 
                              controls 
                              style={{ width: '100%', maxHeight: '220px', borderRadius: '8px', backgroundColor: '#000' }} 
                            />
                            {msg.content && msg.content !== 'Shared video' && (
                              <div style={{ fontSize: '0.8rem', marginTop: '0.3rem' }}>{msg.content}</div>
                            )}
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.9rem', lineHeight: '1.45', wordBreak: 'break-word' }}>
                            {msg.content}
                          </div>
                        )}

                        {/* ATTACHMENT IMAGE PREVIEW */}
                        {msg.mediaUrl && msg.type === 'image' && (
                          <img 
                            src={msg.mediaUrl} 
                            alt="Attachment" 
                            onClick={() => setActiveImageModal(msg.mediaUrl!)}
                            style={{ width: '100%', maxHeight: '220px', objectFit: 'cover', borderRadius: '8px', marginTop: '0.45rem', cursor: 'pointer' }} 
                          />
                        )}

                        {/* REACTION BADGES */}
                        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                          <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.35rem' }}>
                            {Object.entries(msg.reactions).map(([emoji, count]) => (
                              <span key={emoji} style={{ backgroundColor: headerBg, padding: '0.1rem 0.4rem', borderRadius: '10px', fontSize: '0.72rem', border: '1px solid rgba(255,255,255,0.1)' }}>
                                {emoji} {count}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* MESSAGE FOOTER (TIME & ACCURATE TICK MARKS) */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.35rem', marginTop: '0.25rem' }}>
                          <span style={{ fontSize: '0.68rem', color: '#8696a0' }}>{msg.createdAt}</span>
                          {isMe && (
                            <span 
                              title={msg.isRead ? "Read by recipient (Double Blue Ticks)" : isSelectedContactOnline ? "Delivered (Recipient Connected)" : "Sent (Recipient Disconnected/Offline)"}
                              style={{ color: msg.isRead ? '#53bdeb' : '#8696a0', display: 'flex', alignItems: 'center' }}
                            >
                              {msg.isRead || isSelectedContactOnline ? (
                                <CheckCheck size={14} />
                              ) : (
                                <Check size={14} />
                              )}
                            </span>
                          )}
                          <button 
                            onClick={() => setReplyingTo({ id: msg.id, senderName: isMe ? 'You' : (msg.sender?.name || 'Advocate'), content: msg.content })}
                            title="Reply to message" 
                            style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', padding: '0 0.2rem' }}
                          >
                            <Reply size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  });
                })()}
                <div ref={messagesEndRef} />
              </div>

              {/* QUOTED REPLY BANNER */}
              {replyingTo && (
                <div style={{ backgroundColor: headerBg, padding: '0.5rem 1rem', borderTop: `1px solid ${colors.borderThemeColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ borderLeft: `3px solid ${colors.goldPrimary}`, paddingLeft: '0.6rem', fontSize: '0.8rem' }}>
                    <span style={{ fontWeight: '800', color: colors.goldPrimary }}>Replying to {replyingTo.senderName}:</span>
                    <div style={{ color: '#8696a0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '400px' }}>{replyingTo.content}</div>
                  </div>
                  <button onClick={() => setReplyingTo(null)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={16} /></button>
                </div>
              )}

              {/* INPUT BAR (WITH BROADCAST & SUSPENSION ENFORCEMENT) */}
              {(() => {
                const currentGroup = selectedChat.type === 'group' ? groups.find(g => g.id === selectedChat.id) : null;
                const myMemberRecord = currentGroup?.members?.find(m => m.userId === user?.id);
                const isGroupAdmin = currentGroup?.createdBy === user?.id || myMemberRecord?.role === 'admin';
                const isSuspended = myMemberRecord?.isSuspended;

                if (currentGroup?.onlyAdminsCanPost && !isGroupAdmin) {
                  return (
                    <div style={{ backgroundColor: headerBg, padding: '1rem', textAlign: 'center', borderTop: `1px solid ${colors.borderThemeColor}`, color: '#8696a0', fontWeight: 'bold', fontSize: '0.85rem' }}>
                      🔒 Only Group Administrators can send messages. This group is in View-Only broadcast mode.
                    </div>
                  );
                }

                if (isSuspended) {
                  return (
                    <div style={{ backgroundColor: 'rgba(220,38,38,0.12)', padding: '1rem', textAlign: 'center', borderTop: '1px solid #dc2626', color: '#dc2626', fontWeight: 'bold', fontSize: '0.85rem' }}>
                      🚫 You have been temporarily suspended from posting messages in this group.
                    </div>
                  );
                }

                return (
                  <div style={{ backgroundColor: headerBg, borderTop: `1px solid ${colors.borderThemeColor}`, position: 'relative' }}>
                    
                    {/* ATTACHMENT PREVIEW BANNER IF PENDING */}
                    {attachmentPreview && (
                      <div style={{ padding: '0.5rem 1rem', backgroundColor: 'rgba(0,0,0,0.25)', borderBottom: `1px solid ${colors.borderThemeColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'hidden' }}>
                          {attachmentPreview.type === 'image' ? (
                            <img src={attachmentPreview.url} alt="Preview" style={{ width: '42px', height: '42px', borderRadius: '6px', objectFit: 'cover' }} />
                          ) : attachmentPreview.type === 'video' ? (
                            <div style={{ width: '42px', height: '42px', borderRadius: '6px', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#38bdf8' }}>
                              <Film size={22} />
                            </div>
                          ) : attachmentPreview.type === 'audio' ? (
                            <div style={{ width: '42px', height: '42px', borderRadius: '6px', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22c55e' }}>
                              <Volume2 size={22} />
                            </div>
                          ) : (
                            <div style={{ width: '42px', height: '42px', borderRadius: '6px', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#38bdf8' }}>
                              <FileText size={22} />
                            </div>
                          )}
                          <div style={{ overflow: 'hidden' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '320px' }}>
                              {attachmentPreview.file.name}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#8696a0' }}>
                              {(attachmentPreview.file.size / 1024).toFixed(1)} KB • Ready to send
                            </div>
                          </div>
                        </div>
                        <button 
                          onClick={() => setAttachmentPreview(null)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.2rem' }}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}

                    {/* MAIN INPUT CONTROLS BAR */}
                    <div style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                      <button 
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
                        title="Insert Emojis" 
                        style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer' }}
                      >
                        <Smile size={22} />
                      </button>

                      {/* ALL-INCLUSIVE ATTACHMENT MENU BUTTON */}
                      <button 
                        onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                        title="All-Inclusive Attachments (Location, Document, Video, Audio, Citation, Contact)"
                        style={{ 
                          background: showAttachmentMenu ? 'rgba(236,201,75,0.15)' : 'none', 
                          border: 'none', 
                          color: showAttachmentMenu ? colors.goldPrimary : '#8696a0', 
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px'
                        }}
                      >
                        <Paperclip size={22} />
                      </button>

                      {/* HIDDEN FILE INPUTS */}
                      <input 
                        type="file" 
                        ref={imageInputRef} 
                        accept="image/*" 
                        onChange={(e) => handleRichAttachmentSelect('image', e)} 
                        style={{ display: 'none' }} 
                      />
                      <input 
                        type="file" 
                        ref={docInputRef} 
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf" 
                        onChange={(e) => handleRichAttachmentSelect('file', e)} 
                        style={{ display: 'none' }} 
                      />
                      <input 
                        type="file" 
                        ref={videoInputRef} 
                        accept="video/*" 
                        onChange={(e) => handleRichAttachmentSelect('video', e)} 
                        style={{ display: 'none' }} 
                      />
                      <input 
                        type="file" 
                        ref={audioInputRef} 
                        accept="audio/*" 
                        onChange={(e) => handleRichAttachmentSelect('audio', e)} 
                        style={{ display: 'none' }} 
                      />

                      {isRecording ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem', backgroundColor: '#dc2626', color: 'white', padding: '0.45rem 1rem', borderRadius: '20px' }}>
                          <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'white', animation: 'pulse 1s infinite' }}></span>
                          <span style={{ fontWeight: 'bold', fontSize: '0.88rem' }}>Recording Voice Note: 0:{recordingSeconds < 10 ? '0' : ''}{recordingSeconds}</span>
                          <button onClick={handleSendVoiceNote} style={{ marginLeft: 'auto', backgroundColor: 'white', color: '#dc2626', border: 'none', padding: '0.25rem 0.75rem', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}>Send</button>
                          <button onClick={() => setIsRecording(false)} style={{ backgroundColor: 'transparent', color: 'white', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
                        </div>
                      ) : (
                        <form onSubmit={handleSendMessage} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                          <input 
                            type="text" 
                            placeholder="Type a legal message, precedent reference, or attach document..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            style={{ flex: 1, backgroundColor: sidebarBg, border: 'none', borderRadius: '8px', padding: '0.65rem 1rem', color: textColor, outline: 'none', fontSize: '0.9rem' }}
                          />

                          {newMessage.trim() || attachmentPreview ? (
                            <button type="submit" style={{ backgroundColor: '#128c7e', color: 'white', border: 'none', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Send size={18} />
                            </button>
                          ) : (
                            <button type="button" onClick={() => setIsRecording(true)} title="Record Voice Note" style={{ backgroundColor: '#128c7e', color: 'white', border: 'none', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Mic size={18} />
                            </button>
                          )}
                        </form>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ALL-INCLUSIVE ATTACHMENT POPOVER MENU (WHATSAPP/ZOOM STYLE) */}
              {showAttachmentMenu && (
                <div 
                  style={{ 
                    position: 'absolute', 
                    bottom: '75px', 
                    left: '20px', 
                    backgroundColor: '#0f172a', 
                    border: `1.5px solid ${colors.goldPrimary}`, 
                    borderRadius: '16px', 
                    padding: '1rem', 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: '0.85rem', 
                    boxShadow: '0 20px 50px rgba(0,0,0,0.85)', 
                    zIndex: 200,
                    width: '320px',
                    fontFamily: '"Outfit", system-ui, sans-serif'
                  }}
                >
                  {/* 1. Photos & Images */}
                  <button 
                    onClick={() => { setShowAttachmentMenu(false); imageInputRef.current?.click(); }}
                    style={{ background: 'none', border: 'none', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', padding: '0.5rem', borderRadius: '8px', transition: 'background-color 0.15s' }}
                  >
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 4px 12px rgba(2,132,199,0.4)' }}>
                      <Image size={22} />
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>Photos</span>
                  </button>

                  {/* 2. Documents & PDFs */}
                  <button 
                    onClick={() => { setShowAttachmentMenu(false); docInputRef.current?.click(); }}
                    style={{ background: 'none', border: 'none', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', padding: '0.5rem', borderRadius: '8px' }}
                  >
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 4px 12px rgba(124,58,237,0.4)' }}>
                      <FileText size={22} />
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>Document</span>
                  </button>

                  {/* 3. Video Evidence */}
                  <button 
                    onClick={() => { setShowAttachmentMenu(false); videoInputRef.current?.click(); }}
                    style={{ background: 'none', border: 'none', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', padding: '0.5rem', borderRadius: '8px' }}
                  >
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 4px 12px rgba(220,38,38,0.4)' }}>
                      <Film size={22} />
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>Video Clip</span>
                  </button>

                  {/* 4. Audio File */}
                  <button 
                    onClick={() => { setShowAttachmentMenu(false); audioInputRef.current?.click(); }}
                    style={{ background: 'none', border: 'none', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', padding: '0.5rem', borderRadius: '8px' }}
                  >
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 4px 12px rgba(22,163,74,0.4)' }}>
                      <Volume2 size={22} />
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>Audio File</span>
                  </button>

                  {/* 5. GPS & Court Location */}
                  <button 
                    onClick={() => { setShowAttachmentMenu(false); setShowLocationModal(true); }}
                    style={{ background: 'none', border: 'none', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', padding: '0.5rem', borderRadius: '8px' }}
                  >
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 4px 12px rgba(2,132,199,0.4)' }}>
                      <MapPin size={22} />
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>Location</span>
                  </button>

                  {/* 6. Legal Citation / Precedent */}
                  <button 
                    onClick={() => { setShowAttachmentMenu(false); setShowCitationModal(true); }}
                    style={{ background: 'none', border: 'none', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', padding: '0.5rem', borderRadius: '8px' }}
                  >
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#ca8a04', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 4px 12px rgba(202,138,4,0.4)' }}>
                      <Scale size={22} />
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>Citation</span>
                  </button>

                  {/* 7. Advocate Contact Card */}
                  <button 
                    onClick={() => { setShowAttachmentMenu(false); setShowContactShareModal(true); }}
                    style={{ background: 'none', border: 'none', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', padding: '0.5rem', borderRadius: '8px', gridColumn: 'span 3' }}
                  >
                    <div style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <UserCheck size={18} color="#22c55e" />
                      <span style={{ fontSize: '0.78rem', fontWeight: 'bold', color: '#22c55e' }}>Share Advocate Contact Card</span>
                    </div>
                  </button>
                </div>
              )}

              {/* QUICK EMOJI PICKER POPUP */}
              {showEmojiPicker && (
                <div style={{ position: 'absolute', bottom: '70px', left: '15px', backgroundColor: headerBg, border: `1px solid ${colors.borderThemeColor}`, borderRadius: '12px', padding: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', zIndex: 100 }}>
                  {['⚖️', '📜', '👍', '❤️', '🔥', '😂', '🏛️', '💼', '📑', '🙏', '✅', '🛡️'].map(emoji => (
                    <button key={emoji} onClick={() => { setNewMessage(prev => prev + emoji); setShowEmojiPicker(false); }} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer' }}>
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#128c7e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2.5rem', marginBottom: '1rem' }}>
                💬
              </div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '900', color: textColor, margin: 0 }}>Elum Live Chat</h2>
              <p style={{ fontSize: '0.88rem', color: '#8696a0', maxWidth: '420px', margin: '0.5rem 0 1.5rem 0', lineHeight: '1.6' }}>
                [Judicial Officers Secure & Encrypted Network]<br/>
                Select an Advocate, Law Firm Associate, or Special Practice Group from the left panel to begin encrypted real-time communications.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CLICK & DRAG 2D RESIZE HANDLE (HORIZONTAL & VERTICAL) AT BOTTOM-RIGHT CORNER */}
      {isModalView && windowState !== 'fullscreen' && (
        <div 
          onMouseDown={handleStartResize}
          title="Click and drag horizontally or vertically to resize window size"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '20px',
            height: '20px',
            cursor: 'nwse-resize',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.goldPrimary,
            color: 'black',
            borderRadius: '4px 0 14px 0',
            userSelect: 'none'
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>↘</span>
        </div>
      )}

      {/* USER PROFILE & BIO EDIT MODAL (WITH DIRECT DEVICE PHOTO UPLOAD) */}
      {showProfileModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: sidebarBg, padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '480px', color: textColor, boxShadow: '0 20px 40px rgba(0,0,0,0.5)', border: `2px solid ${colors.goldPrimary}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <User size={20} /> Update Profile & Judicial Photo
              </h3>
              <button onClick={() => setShowProfileModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <img src={newAvatarInput || myAvatarUrl} alt="Preview" style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${colors.goldPrimary}`, margin: '0 auto 0.75rem' }} />
              <div style={{ fontSize: '0.8rem', color: '#8696a0' }}>Preview of how your portrait appears across Elum Live Chat</div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>📁 Upload Profile Picture from Device</label>
              <input 
                type="file" 
                accept="image/*"
                onChange={handleProfilePictureUpload} 
                style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: `1px dashed ${colors.goldPrimary}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem', cursor: 'pointer' }} 
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Or Enter Image URL / Preset Avatar</label>
              <input 
                type="text" 
                placeholder="Paste image URL or pick preset below..."
                value={newAvatarInput} 
                onChange={(e) => setNewAvatarInput(e.target.value)} 
                style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, outline: 'none', fontSize: '0.85rem' }} 
              />
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
                {['/owner.jpeg', '/Justice_Yahya_Afridi_CJP.jpeg', '/Justice-Amin-ud-Din-Khan-CJ-FCC.jpeg'].map((url, i) => (
                  <button key={i} type="button" onClick={() => setNewAvatarInput(url)} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', backgroundColor: headerBg, border: `1px solid ${colors.borderThemeColor}`, color: textColor, borderRadius: '4px', cursor: 'pointer' }}>
                    Preset {i + 1}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Brief Professional Description / Bio</label>
              <textarea 
                placeholder="Enter your legal designation (e.g. Senior Advocate Supreme Court • Constitutional Writs & Corporate Practice)..."
                value={newBioInput} 
                onChange={(e) => setNewBioInput(e.target.value)} 
                rows={3}
                style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, outline: 'none', fontSize: '0.85rem', resize: 'vertical' }} 
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={handleSaveProfile} style={{ flex: 1, padding: '0.75rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Save Profile Settings</button>
              <button onClick={() => setShowProfileModal(false)} style={{ flex: 1, padding: '0.75rem', backgroundColor: headerBg, color: textColor, border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* 1-ON-1 CALL FLOATING WINDOW-IN-WINDOW (PiP MODE) */}
      {activeCall && callWindowMode === 'pip' && (
        <div 
          style={{
            position: 'fixed',
            bottom: `${callPipPosition.y}px`,
            right: `${callPipPosition.x}px`,
            width: '410px',
            height: '280px',
            backgroundColor: '#020617',
            borderRadius: '16px',
            border: `2px solid ${colors.goldPrimary}`,
            boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: '"Outfit", system-ui, sans-serif'
          }}
        >
          {/* Draggable PiP Title Bar */}
          <div 
            onMouseDown={handleCallPipMouseDown}
            style={{
              backgroundColor: '#0f172a',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'move',
              userSelect: 'none',
              borderBottom: '1px solid #1e293b',
              color: 'white'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', animation: 'pulse 1.5s infinite' }}></span>
              <span style={{ fontSize: '0.8rem', fontWeight: '800', color: colors.goldPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                {activeCall.name}
              </span>
              <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 'bold' }}>
                • {Math.floor(callDurationSeconds / 60).toString().padStart(2, '0')}:{(callDurationSeconds % 60).toString().padStart(2, '0')}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <button 
                onClick={() => setIs1on1WhiteboardOpen(true)}
                title="Open Legal Whiteboard"
                style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
              >
                <PenTool size={14} />
              </button>
              <button 
                onClick={() => setCallWindowMode('fullscreen')}
                title="Maximize to Full Window"
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
              >
                <Maximize2 size={14} />
              </button>
              <button 
                onClick={() => {
                  if (callStreamRef.current) {
                    callStreamRef.current.getTracks().forEach(t => t.stop());
                    callStreamRef.current = null;
                  }
                  setActiveCall(null);
                  setCallWindowMode('fullscreen');
                }}
                title="End Call"
                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
              >
                <Phone size={14} style={{ transform: 'rotate(135deg)' }} />
              </button>
            </div>
          </div>

          {/* Stage Video in PiP */}
          <div style={{ flex: 1, position: 'relative', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {activeCall.type === 'video' ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <div style={{ textAlign: 'center' }}>
                  <img 
                    src={selectedChat?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(activeCall.name)}&background=128C7E&color=fff`} 
                    alt="Remote User" 
                    style={{ width: '65px', height: '65px', borderRadius: '50%', objectFit: 'cover', border: '2.5px solid #25d366' }} 
                  />
                  <div style={{ fontSize: '0.78rem', color: '#e2e8f0', fontWeight: 'bold', marginTop: '0.3rem' }}>{activeCall.name}</div>
                </div>

                {/* Floating Local Camera Feed in PiP */}
                <div style={{ position: 'absolute', top: '8px', right: '8px', width: '110px', height: '75px', backgroundColor: '#0f172a', borderRadius: '8px', overflow: 'hidden', border: '1.5px solid #38bdf8', zIndex: 10 }}>
                  {!isCallVideoOff ? (
                    <video 
                      ref={callPipVideoRef} 
                      autoPlay 
                      playsInline 
                      muted 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} 
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#94a3b8' }}>
                      Camera Off
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <img 
                  src={selectedChat?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(activeCall.name)}&background=128C7E&color=fff`} 
                  alt="Remote User" 
                  style={{ width: '65px', height: '65px', borderRadius: '50%', objectFit: 'cover', border: '2.5px solid #25d366' }} 
                />
                <div style={{ fontSize: '0.8rem', color: '#e2e8f0', fontWeight: 'bold', marginTop: '0.3rem' }}>{activeCall.name}</div>
              </div>
            )}

            {/* Floating Bottom Action Pills in PiP */}
            <div style={{ position: 'absolute', bottom: '8px', left: '0', right: '0', display: 'flex', justifyContent: 'center', gap: '0.5rem', zIndex: 20 }}>
              <button 
                onClick={() => {
                  if (callStreamRef.current) {
                    callStreamRef.current.getAudioTracks().forEach(t => { t.enabled = isCallMuted; });
                  }
                  setIsCallMuted(!isCallMuted);
                }}
                title={isCallMuted ? "Unmute Mic" : "Mute Mic"}
                style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: isCallMuted ? '#dc2626' : 'rgba(15,23,42,0.85)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {isCallMuted ? <MicOff size={14} /> : <Mic size={14} />}
              </button>

              <button 
                onClick={() => {
                  if (callStreamRef.current) {
                    callStreamRef.current.getVideoTracks().forEach(t => { t.enabled = isCallVideoOff; });
                  }
                  setIsCallVideoOff(!isCallVideoOff);
                }}
                title={isCallVideoOff ? "Turn Video On" : "Turn Video Off"}
                style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: isCallVideoOff ? '#dc2626' : 'rgba(15,23,42,0.85)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {isCallVideoOff ? <VideoOff size={14} /> : <Video size={14} />}
              </button>

              <button 
                onClick={() => setIs1on1WhiteboardOpen(true)}
                title="Open Legal Whiteboard (PiP / Fullscreen)"
                style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(15,23,42,0.85)', color: colors.goldPrimary, border: `1px solid ${colors.goldPrimary}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <PenTool size={14} />
              </button>

              <button 
                onClick={() => setCallWindowMode('fullscreen')}
                title="Maximize to Full Window"
                style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(15,23,42,0.85)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Maximize2 size={14} />
              </button>

              <button 
                onClick={() => {
                  if (callStreamRef.current) {
                    callStreamRef.current.getTracks().forEach(t => t.stop());
                    callStreamRef.current = null;
                  }
                  setActiveCall(null);
                  setCallWindowMode('fullscreen');
                }}
                title="Disconnect Call"
                style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#dc2626', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Phone size={15} style={{ transform: 'rotate(135deg)' }} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FULL WINDOW / NORMAL OVERLAY (1-ON-1 & GROUP CALLS) */}
      {activeCall && callWindowMode === 'fullscreen' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(5, 10, 20, 0.96)', zIndex: 99999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: '"Outfit", system-ui, sans-serif', padding: '2rem' }}>
          
          {/* Header Info & Call Timer & Window Control Buttons */}
          <div style={{ width: '100%', maxWidth: '850px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', zIndex: 10 }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'rgba(34, 197, 94, 0.15)', border: '1px solid #22c55e', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '800', color: '#22c55e', marginBottom: '0.4rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', animation: 'pulse 1.5s infinite' }}></span>
                {activeCall.type === 'voice' ? '📞 Direct Judicial Voice Call' : '📹 Dedicated 1-on-1 HD Video Call'} • 256-Bit TLS
              </div>
              <h2 style={{ fontSize: '1.6rem', fontWeight: '900', margin: 0, color: colors.goldPrimary }}>
                {activeCall.isGroup ? `🏛️ ${activeCall.name} Conference` : activeCall.name}
              </h2>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.88rem', fontWeight: '700', backgroundColor: '#0f172a', padding: '0.4rem 0.85rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                Duration: <span style={{ color: 'white' }}>{Math.floor(callDurationSeconds / 60).toString().padStart(2, '0')}:{(callDurationSeconds % 60).toString().padStart(2, '0')}</span>
              </div>

              {/* PiP & Whiteboard Top Controls */}
              <button 
                onClick={() => setIs1on1WhiteboardOpen(true)}
                title="Open Legal Whiteboard"
                style={{ backgroundColor: '#0f172a', color: colors.goldPrimary, border: `1px solid ${colors.goldPrimary}`, borderRadius: '8px', padding: '0.45rem 0.85rem', fontSize: '0.82rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              >
                <PenTool size={15} /> Whiteboard
              </button>

              <button 
                onClick={() => setCallWindowMode('pip')}
                title="Switch to Window-in-Window (PiP Mode)"
                style={{ backgroundColor: '#0f172a', color: '#38bdf8', border: '1px solid #38bdf8', borderRadius: '8px', padding: '0.45rem 0.85rem', fontSize: '0.82rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              >
                <PictureInPicture size={15} /> PiP Mode
              </button>
            </div>
          </div>

          {/* 1-ON-1 VIDEO CALL VIEWPORT */}
          {!activeCall.isGroup && activeCall.type === 'video' ? (
            <div style={{ position: 'relative', width: '100%', maxWidth: '850px', aspectRatio: '16/9', backgroundColor: '#020617', borderRadius: '20px', overflow: 'hidden', border: `2px solid ${colors.goldPrimary}`, boxShadow: '0 20px 50px rgba(0,0,0,0.8)', marginBottom: '1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              
              {/* REMOTE PARTICIPANT STAGE */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                <div style={{ position: 'relative' }}>
                  <img 
                    src={selectedChat?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(activeCall.name)}&background=128C7E&color=fff`} 
                    alt="Remote User" 
                    style={{ width: '110px', height: '110px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #25d366', boxShadow: '0 0 25px rgba(37,211,102,0.5)' }} 
                  />
                  <span style={{ position: 'absolute', bottom: '4px', right: '4px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#25d366', border: '3px solid black' }}></span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#f8fafc' }}>{activeCall.name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#38bdf8' }}>Advocate Practitioner • High Court & Supreme Court</div>
                </div>
              </div>

              {/* LOCAL USER FLOATING PREVIEW (TOP RIGHT) */}
              <div style={{ position: 'absolute', top: '16px', right: '16px', width: '200px', height: '135px', backgroundColor: '#0f172a', borderRadius: '12px', overflow: 'hidden', border: '2px solid #38bdf8', boxShadow: '0 8px 24px rgba(0,0,0,0.6)', zIndex: 20 }}>
                {!isCallVideoOff ? (
                  <video 
                    ref={callLocalVideoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} 
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', backgroundColor: '#090d16' }}>
                    <img src={myAvatarUrl} alt="Me" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                    <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Camera Off</span>
                  </div>
                )}
                <div style={{ position: 'absolute', bottom: '4px', left: '6px', backgroundColor: 'rgba(0,0,0,0.6)', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.65rem', color: 'white' }}>
                  {user?.name || 'Me'}
                </div>
              </div>

            </div>
          ) : activeCall.isGroup ? (
            /* GROUP CONFERENCE TILES */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.5rem', width: '100%', maxWidth: '800px', maxHeight: '50vh', overflowY: 'auto', marginBottom: '2rem', padding: '1rem', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
              {(activeCall.participants && activeCall.participants.length > 0 ? activeCall.participants : [{ user: { name: 'Advocate Host', role: 'admin' } }]).map((p, idx) => (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '12px', border: '1px solid #25d366', position: 'relative' }}>
                  <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
                    <img 
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(p.user?.name || 'Advocate')}&background=128C7E&color=fff`} 
                      alt="Participant" 
                      style={{ width: '65px', height: '65px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #25d366' }} 
                    />
                    <span style={{ position: 'absolute', bottom: 0, right: 0, width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#25d366', border: '2px solid black' }}></span>
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '0.88rem', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{p.user?.name || 'Advocate'}</div>
                  <div style={{ fontSize: '0.7rem', color: colors.goldPrimary, textTransform: 'capitalize' }}>{p.role || 'Member'}</div>
                </div>
              ))}
            </div>
          ) : (
            /* 1-ON-1 VOICE CALL AVATAR VIEW */
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={selectedChat?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(activeCall.name)}&background=128C7E&color=fff`} alt="Call Avatar" style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #25d366', marginBottom: '1rem', boxShadow: '0 0 30px rgba(37,211,102,0.4)' }} />
                <span style={{ position: 'absolute', bottom: '15px', right: '10px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#25d366', border: '3px solid black' }}></span>
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{activeCall.name}</div>
            </div>
          )}

          {/* CALL CONTROLS BAR */}
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
            <button 
              onClick={() => {
                if (callStreamRef.current) {
                  callStreamRef.current.getAudioTracks().forEach(t => { t.enabled = isCallMuted; });
                }
                setIsCallMuted(!isCallMuted);
              }}
              title={isCallMuted ? "Unmute Microphone" : "Mute Microphone"}
              style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: isCallMuted ? '#dc2626' : 'rgba(255,255,255,0.2)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isCallMuted ? <MicOff size={22} /> : <Mic size={22} />}
            </button>

            <button 
              onClick={() => {
                if (callStreamRef.current) {
                  callStreamRef.current.getVideoTracks().forEach(t => { t.enabled = isCallVideoOff; });
                }
                setIsCallVideoOff(!isCallVideoOff);
              }}
              title={isCallVideoOff ? "Turn Video Camera On" : "Turn Video Camera Off"}
              style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: isCallVideoOff ? '#dc2626' : 'rgba(255,255,255,0.2)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isCallVideoOff ? <VideoOff size={22} /> : <Video size={22} />}
            </button>

            <button 
              onClick={() => setIs1on1WhiteboardOpen(true)}
              title="Open Legal Whiteboard (Touch & Mouse Drawing)"
              style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', color: colors.goldPrimary, border: `1.5px solid ${colors.goldPrimary}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <PenTool size={22} />
            </button>

            <button 
              onClick={() => setIsCallScreenSharing(!isCallScreenSharing)}
              title={isCallScreenSharing ? "Stop Screen Share" : "Share Screen / Legal Draft"}
              style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: isCallScreenSharing ? colors.goldPrimary : 'rgba(255,255,255,0.2)', color: isCallScreenSharing ? 'black' : 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Maximize2 size={22} />
            </button>

            <button 
              onClick={() => setCallWindowMode('pip')}
              title="Window-in-Window (PiP Mode)"
              style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', color: '#38bdf8', border: '1.5px solid #38bdf8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <PictureInPicture size={22} />
            </button>

            <button 
              onClick={() => { 
                if (callStreamRef.current) {
                  callStreamRef.current.getTracks().forEach(t => t.stop());
                  callStreamRef.current = null;
                }
                setActiveCall(null); 
                setIsCallMuted(false); 
                setIsCallVideoOff(false); 
                setIsCallScreenSharing(false); 
                setCallWindowMode('fullscreen');
              }} 
              title="Disconnect Call"
              style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#dc2626', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(220,38,38,0.5)' }}
            >
              <Phone size={26} style={{ transform: 'rotate(135deg)' }} />
            </button>
          </div>
        </div>
      )}

      {/* GROUP MANAGEMENT MODAL (ADD, REMOVE, SUSPEND, ROLES, BROADCAST) */}
      {showGroupManageModal && selectedChat && selectedChat.type === 'group' && (() => {
        const currentGroup: Group = groups.find(g => g.id === selectedChat.id) || {
          id: selectedChat.id,
          name: selectedChat.name,
          description: selectedChat.detail,
          avatarUrl: selectedChat.avatarUrl,
          createdBy: user?.id || 'me',
          onlyAdminsCanPost: false,
          members: []
        };
        const myMemberRecord = currentGroup?.members?.find(m => m.userId === user?.id);
        const isGroupAdmin = !currentGroup?.createdBy || currentGroup?.createdBy === user?.id || myMemberRecord?.role === 'admin' || user?.role === 'admin' || user?.role === 'superuser';
        const nonMemberContacts = contacts.filter(c => !currentGroup?.members?.some(m => m.userId === c.id));

        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
            <div style={{ backgroundColor: sidebarBg, padding: '1.75rem', borderRadius: '16px', width: '100%', maxWidth: '580px', maxHeight: '85vh', overflowY: 'auto', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.6)', border: `2px solid ${colors.goldPrimary}` }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: `1px solid ${colors.borderThemeColor}`, paddingBottom: '0.75rem' }}>
                <div>
                  <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Users size={20} /> {currentGroup?.name}
                  </h3>
                  <div style={{ fontSize: '0.75rem', color: '#8696a0' }}>{currentGroup?.description || 'Practice Group Channel'}</div>
                </div>
                <button onClick={() => setShowGroupManageModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
              </div>

              {/* ADMIN BROADCAST MODE SETTING */}
              {isGroupAdmin && (
                <div style={{ backgroundColor: headerBg, padding: '0.85rem 1rem', borderRadius: '10px', marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.88rem' }}>📢 Broadcast Mode (View-Only)</div>
                    <div style={{ fontSize: '0.72rem', color: '#8696a0' }}>When enabled, only Group Admins can share messages; all other members have View-Only access.</div>
                  </div>
                  <button 
                    onClick={() => handleToggleBroadcastInGroup(!currentGroup?.onlyAdminsCanPost)}
                    style={{ padding: '0.4rem 0.85rem', backgroundColor: currentGroup?.onlyAdminsCanPost ? '#dc2626' : '#128c7e', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: '0.78rem', cursor: 'pointer' }}
                  >
                    {currentGroup?.onlyAdminsCanPost ? 'Disable Broadcast' : 'Enable Broadcast'}
                  </button>
                </div>
              )}

              {/* ADD NEW MEMBER SECTION (ALWAYS ACCESSIBLE TO GROUP MEMBERS/ADMINS) */}
              <div style={{ backgroundColor: headerBg, padding: '0.85rem 1rem', borderRadius: '10px', marginBottom: '1.25rem', border: `1px dashed ${colors.goldPrimary}` }}>
                <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.5rem', color: colors.goldPrimary, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <UserPlus size={16} /> Add Advocate / Colleague to {currentGroup?.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: sidebarBg, border: `1px solid ${colors.borderThemeColor}`, borderRadius: '6px', padding: '0.35rem 0.65rem', marginBottom: '0.5rem' }}>
                  <Search size={14} color={colors.goldPrimary} />
                  <input
                    type="text"
                    placeholder="Search non-members by name, role, email..."
                    value={manageMemberSearch}
                    onChange={(e) => setManageMemberSearch(e.target.value)}
                    style={{ flex: 1, backgroundColor: 'transparent', border: 'none', color: textColor, outline: 'none', fontSize: '0.8rem' }}
                  />
                  {manageMemberSearch && (
                    <button
                      type="button"
                      onClick={() => setManageMemberSearch('')}
                      style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', padding: 0 }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <select 
                    value={selectedNewMemberId} 
                    onChange={(e) => setSelectedNewMemberId(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: sidebarBg, color: textColor, fontSize: '0.85rem' }}
                  >
                    <option value="">Select an advocate from contact directory...</option>
                    {nonMemberContacts
                      .filter(c => {
                        if (!manageMemberSearch.trim()) return true;
                        const q = manageMemberSearch.toLowerCase();
                        return (
                          (c.name && c.name.toLowerCase().includes(q)) ||
                          (c.email && c.email.toLowerCase().includes(q)) ||
                          (c.role && c.role.toLowerCase().includes(q)) ||
                          (c.phone && c.phone.includes(q))
                        );
                      })
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.role.replace('_', ' ')}) • {c.email}</option>
                      ))}
                  </select>
                  <button 
                    onClick={handleAddMemberToGroup}
                    disabled={!selectedNewMemberId}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', opacity: selectedNewMemberId ? 1 : 0.5 }}
                  >
                    Add Member
                  </button>
                </div>
              </div>

              {/* CURRENT MEMBERS LIST */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontWeight: 'bold', fontSize: '0.88rem', marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Group Members ({currentGroup?.members?.length || 0})</span>
                  <span style={{ fontSize: '0.75rem', color: colors.goldPrimary }}>{currentGroup?.onlyAdminsCanPost ? '📢 Broadcast Enabled' : '💬 Open Chat'}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                  {currentGroup?.members?.map((m) => {
                    const isMemberAdmin = m.role === 'admin' || m.userId === currentGroup.createdBy;
                    const isSelf = m.userId === user?.id;

                    return (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 0.85rem', backgroundColor: headerBg, borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                          <img 
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(m.user?.name || 'Advocate')}&background=128C7E&color=fff`} 
                            alt="Avatar" 
                            style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' }} 
                          />
                          <div>
                            <div style={{ fontWeight: 'bold', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              {m.user?.name || 'Advocate Member'} {isSelf && '(You)'}
                              {isMemberAdmin && <span style={{ fontSize: '0.65rem', backgroundColor: colors.goldPrimary, color: 'black', padding: '0.1rem 0.35rem', borderRadius: '4px', fontWeight: '800' }}>👑 Admin</span>}
                              {m.isSuspended && <span style={{ fontSize: '0.65rem', backgroundColor: '#dc2626', color: 'white', padding: '0.1rem 0.35rem', borderRadius: '4px', fontWeight: '800' }}>🚫 Suspended</span>}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#8696a0' }}>{m.user?.role?.replace('_', ' ') || 'Practitioner'} • {m.user?.email}</div>
                          </div>
                        </div>

                        {/* MEMBER CONTROLS (IF CURRENT USER IS ADMIN AND NOT ACTING ON SELF) */}
                        {isGroupAdmin && !isSelf && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <button 
                              onClick={() => handleSetMemberRoleInGroup(m.userId, m.role === 'admin' ? 'member' : 'admin')}
                              title={m.role === 'admin' ? "Demote to Member" : "Promote to Group Admin"}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: 'transparent', border: `1px solid ${colors.goldPrimary}`, color: colors.goldPrimary, borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              {m.role === 'admin' ? 'Demote' : 'Make Admin'}
                            </button>

                            <button 
                              onClick={() => handleSuspendMemberInGroup(m.userId, !m.isSuspended)}
                              title={m.isSuspended ? "Reinstate Chat Permissions" : "Suspend from Chatting"}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: m.isSuspended ? '#25d366' : '#eab308', color: 'black', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              {m.isSuspended ? 'Reinstate' : 'Suspend'}
                            </button>

                            <button 
                              onClick={() => setMemberToDelete({ id: m.userId, name: m.user?.name || 'Member' })}
                              title="Remove Member from Group"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CONFIRM MEMBER DELETION DIALOG */}
              {memberToDelete && (
                <div style={{ backgroundColor: 'rgba(220,38,38,0.1)', padding: '1rem', borderRadius: '10px', border: '1px solid #dc2626', marginBottom: '1.25rem' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '0.88rem', color: '#dc2626', marginBottom: '0.4rem' }}>
                    Confirm Removal: {memberToDelete.name}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                    <input 
                      type="checkbox" 
                      checked={notifyOnDelete} 
                      onChange={(e) => setNotifyOnDelete(e.target.checked)} 
                    />
                    Notify group members about this removal in chat log
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      onClick={() => handleRemoveMemberFromGroup(memberToDelete.id, notifyOnDelete)}
                      style={{ padding: '0.45rem 0.85rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' }}
                    >
                      Yes, Remove Member
                    </button>
                    <button 
                      onClick={() => setMemberToDelete(null)}
                      style={{ padding: '0.45rem 0.85rem', backgroundColor: headerBg, color: textColor, border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={{ textAlign: 'right' }}>
                <button onClick={() => setShowGroupManageModal(false)} style={{ padding: '0.65rem 1.5rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                  Done / Close
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* FULL IMAGE VIEW MODAL */}
      {activeImageModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <button onClick={() => setActiveImageModal(null)} style={{ position: 'absolute', top: '20px', right: '20px', backgroundColor: 'transparent', color: 'white', border: 'none', cursor: 'pointer' }}><X size={32} /></button>
          <img src={activeImageModal} alt="Expanded Preview" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px', objectFit: 'contain' }} />
        </div>
      )}

      {/* ENHANCED GROUP CREATION MODAL (PERSISTS TO DB WITH MEMBER SELECTION & BROADCAST TOGGLE) */}
      {showGroupModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: sidebarBg, padding: '2rem', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.6)', border: `2px solid ${colors.goldPrimary}` }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users size={22} /> Create Practice Group / Bar Council Channel
              </h3>
              <button onClick={() => setShowGroupModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleCreateGroup}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Group Name *</label>
                <input 
                  type="text" 
                  placeholder="e.g. Quetta Bar Council, SC Tax Bench" 
                  value={newGroupName} 
                  onChange={(e) => setNewGroupName(e.target.value)} 
                  required
                  style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, outline: 'none', fontSize: '0.9rem' }} 
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Group Description / Jurisdiction</label>
                <input 
                  type="text" 
                  placeholder="e.g. Bar Council Coordination & Urgent Roster Notice Channel" 
                  value={newGroupDesc} 
                  onChange={(e) => setNewGroupDesc(e.target.value)} 
                  style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, outline: 'none', fontSize: '0.85rem' }} 
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>📁 Group Emblem / Avatar (Device Upload or Preset)</label>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handleGroupAvatarUpload} 
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: `1px dashed ${colors.goldPrimary}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem', cursor: 'pointer', marginBottom: '0.4rem' }} 
                />
              </div>

              <div style={{ marginBottom: '1.25rem', backgroundColor: headerBg, padding: '0.75rem 1rem', borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}>
                  <input 
                    type="checkbox" 
                    checked={newGroupOnlyAdmins} 
                    onChange={(e) => setNewGroupOnlyAdmins(e.target.checked)} 
                  />
                  📢 Broadcast Mode (Only Group Admins can post • View-Only for members)
                </label>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                    Select Initial Advocates / Members ({selectedInitialMemberIds.length} Selected)
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const filteredIds = contacts
                          .filter(c => {
                            if (!groupMemberSearch.trim()) return true;
                            const q = groupMemberSearch.toLowerCase();
                            return (
                              (c.name && c.name.toLowerCase().includes(q)) ||
                              (c.email && c.email.toLowerCase().includes(q)) ||
                              (c.role && c.role.toLowerCase().includes(q)) ||
                              (c.phone && c.phone.includes(q))
                            );
                          })
                          .map(c => c.id);
                        setSelectedInitialMemberIds(prev => Array.from(new Set([...prev, ...filteredIds])));
                      }}
                      style={{ fontSize: '0.72rem', backgroundColor: 'rgba(18,140,126,0.15)', color: '#128c7e', border: '1px solid #128c7e', borderRadius: '4px', padding: '0.15rem 0.45rem', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      Select Filtered
                    </button>
                    {selectedInitialMemberIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedInitialMemberIds([])}
                        style={{ fontSize: '0.72rem', backgroundColor: 'rgba(220,38,38,0.15)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '4px', padding: '0.15rem 0.45rem', cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>

                {/* SEARCH INPUT BAR FOR MEMBERS */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: headerBg, border: `1px solid ${colors.borderThemeColor}`, borderRadius: '8px', padding: '0.45rem 0.75rem', marginBottom: '0.65rem' }}>
                  <Search size={16} color={colors.goldPrimary} />
                  <input
                    type="text"
                    placeholder="Search advocates by name, role, or email..."
                    value={groupMemberSearch}
                    onChange={(e) => setGroupMemberSearch(e.target.value)}
                    style={{ flex: 1, backgroundColor: 'transparent', border: 'none', color: textColor, outline: 'none', fontSize: '0.82rem' }}
                  />
                  {groupMemberSearch && (
                    <button
                      type="button"
                      onClick={() => setGroupMemberSearch('')}
                      style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', padding: 0 }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* SELECTED CHIPS ROW */}
                {selectedInitialMemberIds.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.65rem', maxHeight: '75px', overflowY: 'auto', padding: '0.35rem', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                    {selectedInitialMemberIds.map(id => {
                      const c = contacts.find(contact => contact.id === id);
                      return (
                        <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', backgroundColor: '#128c7e', color: 'white', fontSize: '0.72rem', fontWeight: '600', padding: '0.2rem 0.5rem', borderRadius: '12px' }}>
                          <span>{c?.name || 'Advocate'}</span>
                          <X
                            size={12}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedInitialMemberIds(prev => prev.filter(x => x !== id))}
                          />
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* MEMBER CHECKBOX LIST */}
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: `1px solid ${colors.borderThemeColor}`, borderRadius: '8px', padding: '0.4rem', backgroundColor: headerBg }}>
                  {(() => {
                    const filtered = contacts.filter(contact => {
                      if (!groupMemberSearch.trim()) return true;
                      const q = groupMemberSearch.toLowerCase();
                      return (
                        (contact.name && contact.name.toLowerCase().includes(q)) ||
                        (contact.email && contact.email.toLowerCase().includes(q)) ||
                        (contact.role && contact.role.toLowerCase().includes(q)) ||
                        (contact.phone && contact.phone.includes(q))
                      );
                    });

                    if (filtered.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '1rem', color: '#8696a0', fontSize: '0.8rem' }}>
                          No advocates found matching "{groupMemberSearch}"
                        </div>
                      );
                    }

                    return filtered.map(contact => {
                      const isSelected = selectedInitialMemberIds.includes(contact.id);
                      return (
                        <label
                          key={contact.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '0.65rem',
                            padding: '0.45rem 0.65rem',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            backgroundColor: isSelected ? 'rgba(18,140,126,0.15)' : 'transparent',
                            marginBottom: '0.2rem',
                            borderBottom: `1px solid ${colors.borderThemeColor}`
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedInitialMemberIds(prev => [...prev, contact.id]);
                                } else {
                                  setSelectedInitialMemberIds(prev => prev.filter(id => id !== contact.id));
                                }
                              }}
                            />
                            <img
                              src={contact.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&background=128C7E&color=fff`}
                              alt=""
                              style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <div>
                              <div style={{ fontSize: '0.84rem', fontWeight: 'bold', color: textColor }}>
                                {contact.name}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#8696a0' }}>
                                {contact.email} {contact.phone ? `• ${contact.phone}` : ''}
                              </div>
                            </div>
                          </div>
                          <span style={{ fontSize: '0.68rem', backgroundColor: 'rgba(236,201,75,0.15)', color: colors.goldPrimary, padding: '0.15rem 0.45rem', borderRadius: '4px', fontWeight: '700', textTransform: 'capitalize', flexShrink: 0 }}>
                            {contact.role?.replace('_', ' ') || 'Advocate'}
                          </span>
                        </label>
                      );
                    });
                  })()}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.75rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                  Create & Save Group
                </button>
                <button type="button" onClick={() => setShowGroupModal(false)} style={{ flex: 1, padding: '0.75rem', backgroundColor: headerBg, color: textColor, border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* ENHANCED STATUS DISPATCH MODAL (IMAGE, VIDEO CLIP, URL & AUDIENCE TARGETING) */}
      {showStatusModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: sidebarBg, padding: '1.75rem', borderRadius: '18px', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.7)', border: `2px solid ${colors.goldPrimary}` }}>
            
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: `1px solid ${colors.borderThemeColor}`, paddingBottom: '0.75rem' }}>
              <div>
                <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⭕ Post 24h Legal Status Dispatch
                </h3>
                <div style={{ fontSize: '0.75rem', color: '#8696a0' }}>Broadcast case precedents, updates, images, video clips, or URLs</div>
              </div>
              <button onClick={() => setShowStatusModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            {/* 1. MEDIA TYPE SELECTOR TABS */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', marginBottom: '0.45rem', color: colors.goldPrimary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                1. Select Dispatch Media Format:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem', backgroundColor: headerBg, padding: '0.3rem', borderRadius: '10px' }}>
                <button
                  type="button"
                  onClick={() => setStatusMediaType('text')}
                  style={{ padding: '0.5rem 0.2rem', borderRadius: '8px', border: 'none', backgroundColor: statusMediaType === 'text' ? '#128c7e' : 'transparent', color: statusMediaType === 'text' ? 'white' : textColor, fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}
                >
                  <Edit3 size={15} /> <span>✍️ Text</span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusMediaType('image')}
                  style={{ padding: '0.5rem 0.2rem', borderRadius: '8px', border: 'none', backgroundColor: statusMediaType === 'image' ? '#128c7e' : 'transparent', color: statusMediaType === 'image' ? 'white' : textColor, fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}
                >
                  <Image size={15} /> <span>🖼️ Image</span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusMediaType('video')}
                  style={{ padding: '0.5rem 0.2rem', borderRadius: '8px', border: 'none', backgroundColor: statusMediaType === 'video' ? '#128c7e' : 'transparent', color: statusMediaType === 'video' ? 'white' : textColor, fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}
                >
                  <Film size={15} /> <span>🎥 Video Clip</span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusMediaType('url')}
                  style={{ padding: '0.5rem 0.2rem', borderRadius: '8px', border: 'none', backgroundColor: statusMediaType === 'url' ? '#128c7e' : 'transparent', color: statusMediaType === 'url' ? 'white' : textColor, fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}
                >
                  <Link2 size={15} /> <span>🔗 Precedent URL</span>
                </button>
              </div>
            </div>

            {/* 2. MEDIA UPLOAD / INPUT CONTROLS */}
            {statusMediaType === 'image' && (
              <div style={{ backgroundColor: headerBg, padding: '1rem', borderRadius: '10px', marginBottom: '1.25rem', border: `1px solid ${colors.borderThemeColor}` }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Upload Image File or Enter URL:</label>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (re) => {
                        setNewStatusMediaUrl(re.target?.result as string);
                        setStatusMediaPreview({ url: re.target?.result as string, type: 'image' });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '8px', border: `1px dashed ${colors.goldPrimary}`, backgroundColor: sidebarBg, color: textColor, fontSize: '0.82rem', cursor: 'pointer', marginBottom: '0.5rem' }} 
                />
                <input 
                  type="text" 
                  placeholder="Or paste image URL (https://...)" 
                  value={newStatusMediaUrl.startsWith('data:') ? '' : newStatusMediaUrl}
                  onChange={(e) => {
                    setNewStatusMediaUrl(e.target.value);
                    setStatusMediaPreview({ url: e.target.value, type: 'image' });
                  }}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: sidebarBg, color: textColor, outline: 'none', fontSize: '0.82rem' }}
                />

                {newStatusMediaUrl && (
                  <div style={{ marginTop: '0.75rem', position: 'relative', textAlign: 'center' }}>
                    <img src={newStatusMediaUrl} alt="Preview" style={{ maxHeight: '180px', maxWidth: '100%', borderRadius: '8px', objectFit: 'contain', border: '1px solid #334155' }} />
                    <button 
                      onClick={() => { setNewStatusMediaUrl(''); setStatusMediaPreview(null); }}
                      style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <X size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {statusMediaType === 'video' && (
              <div style={{ backgroundColor: headerBg, padding: '1rem', borderRadius: '10px', marginBottom: '1.25rem', border: `1px solid ${colors.borderThemeColor}` }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Upload Video Clip (.mp4, .webm, .mov) or Enter Video URL:</label>
                <input 
                  type="file" 
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (re) => {
                        setNewStatusMediaUrl(re.target?.result as string);
                        setStatusMediaPreview({ url: re.target?.result as string, type: 'video' });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '8px', border: `1px dashed #38bdf8`, backgroundColor: sidebarBg, color: textColor, fontSize: '0.82rem', cursor: 'pointer', marginBottom: '0.5rem' }} 
                />
                <input 
                  type="text" 
                  placeholder="Or paste video clip URL (https://...)" 
                  value={newStatusMediaUrl.startsWith('data:') ? '' : newStatusMediaUrl}
                  onChange={(e) => {
                    setNewStatusMediaUrl(e.target.value);
                    setStatusMediaPreview({ url: e.target.value, type: 'video' });
                  }}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: sidebarBg, color: textColor, outline: 'none', fontSize: '0.82rem' }}
                />

                {newStatusMediaUrl && (
                  <div style={{ marginTop: '0.75rem', position: 'relative' }}>
                    <video src={newStatusMediaUrl} controls style={{ width: '100%', maxHeight: '180px', borderRadius: '8px', backgroundColor: 'black' }} />
                    <button 
                      onClick={() => { setNewStatusMediaUrl(''); setStatusMediaPreview(null); }}
                      style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <X size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {statusMediaType === 'url' && (
              <div style={{ backgroundColor: headerBg, padding: '1rem', borderRadius: '10px', marginBottom: '1.25rem', border: `1px solid ${colors.borderThemeColor}` }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Precedent / Case Law / Statutory Reference URL:</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: sidebarBg, padding: '0.5rem 0.75rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}` }}>
                  <Link2 size={16} color="#c084fc" />
                  <input 
                    type="url" 
                    placeholder="https://www.elumlaw.com/law-directory?query=2026+SCMR+104"
                    value={newStatusLinkUrl}
                    onChange={(e) => setNewStatusLinkUrl(e.target.value)}
                    style={{ width: '100%', backgroundColor: 'transparent', border: 'none', color: textColor, outline: 'none', fontSize: '0.82rem' }}
                  />
                </div>
                {newStatusLinkUrl && (
                  <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.85rem', backgroundColor: 'rgba(124, 58, 237, 0.15)', borderRadius: '8px', border: '1px solid #7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '0.78rem', color: '#c084fc', fontWeight: 'bold', wordBreak: 'break-all' }}>
                      🔗 {newStatusLinkUrl}
                    </div>
                    <ExternalLink size={14} color="#c084fc" />
                  </div>
                )}
              </div>
            )}

            {/* 3. CAPTION / STATUS TEXT */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', marginBottom: '0.35rem', color: colors.goldPrimary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                2. Status Dispatch Text / Caption:
              </label>
              <textarea 
                placeholder={statusMediaType === 'text' ? "Write your legal dispatch announcement or precedent notice..." : "Add a caption or citation summary..."}
                value={newStatusText} 
                onChange={(e) => setNewStatusText(e.target.value)} 
                rows={statusMediaType === 'text' ? 4 : 2}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, outline: 'none', fontSize: '0.88rem', resize: 'vertical' }} 
              />
            </div>

            {/* 4. PRIVACY / AUDIENCE TARGETING SELECTOR */}
            <div style={{ backgroundColor: headerBg, padding: '1rem', borderRadius: '10px', marginBottom: '1.5rem', border: `1px solid ${colors.borderThemeColor}` }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', marginBottom: '0.5rem', color: colors.goldPrimary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                3. Privacy & Audience Visibility:
              </label>
              
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.75rem', backgroundColor: statusTargetAudience === 'all' ? 'rgba(34, 197, 94, 0.15)' : sidebarBg, borderRadius: '8px', border: statusTargetAudience === 'all' ? '1.5px solid #22c55e' : `1px solid ${colors.borderThemeColor}`, cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="statusAudience" 
                    checked={statusTargetAudience === 'all'} 
                    onChange={() => setStatusTargetAudience('all')} 
                  />
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: '800', color: '#22c55e' }}>🌐 All Users</div>
                    <div style={{ fontSize: '0.68rem', color: '#8696a0' }}>Visible to all advocates & bar members</div>
                  </div>
                </label>

                <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.75rem', backgroundColor: statusTargetAudience === 'specific' ? 'rgba(234, 179, 8, 0.15)' : sidebarBg, borderRadius: '8px', border: statusTargetAudience === 'specific' ? '1.5px solid #eab308' : `1px solid ${colors.borderThemeColor}`, cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="statusAudience" 
                    checked={statusTargetAudience === 'specific'} 
                    onChange={() => setStatusTargetAudience('specific')} 
                  />
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: '800', color: '#eab308' }}>🔒 Specific Users</div>
                    <div style={{ fontSize: '0.68rem', color: '#8696a0' }}>Only selected contacts can view</div>
                  </div>
                </label>
              </div>

              {/* SPECIFIC CONTACTS CHECKLIST (IF 'SPECIFIC' SELECTED) */}
              {statusTargetAudience === 'specific' && (
                <div style={{ borderTop: `1px solid ${colors.borderThemeColor}`, paddingTop: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                      Select Permitted Advocates ({statusAllowedUserIds.length} Selected)
                    </span>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button 
                        type="button" 
                        onClick={() => setStatusAllowedUserIds(contacts.map(c => c.id))}
                        style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Select All
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setStatusAllowedUserIds([])}
                        style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <input 
                    type="text" 
                    placeholder="Search contacts..." 
                    value={statusAudienceSearch}
                    onChange={(e) => setStatusAudienceSearch(e.target.value)}
                    style={{ width: '100%', padding: '0.4rem 0.65rem', borderRadius: '6px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: sidebarBg, color: textColor, outline: 'none', fontSize: '0.78rem', marginBottom: '0.5rem' }}
                  />

                  <div style={{ maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {contacts
                      .filter(c => !statusAudienceSearch || c.name.toLowerCase().includes(statusAudienceSearch.toLowerCase()) || c.email.toLowerCase().includes(statusAudienceSearch.toLowerCase()))
                      .map(c => {
                        const isChecked = statusAllowedUserIds.includes(c.id);
                        return (
                          <label key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.35rem 0.5rem', borderRadius: '6px', backgroundColor: isChecked ? 'rgba(18, 140, 126, 0.15)' : sidebarBg, cursor: 'pointer', fontSize: '0.78rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input 
                                type="checkbox" 
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setStatusAllowedUserIds(prev => [...prev, c.id]);
                                  } else {
                                    setStatusAllowedUserIds(prev => prev.filter(id => id !== c.id));
                                  }
                                }}
                              />
                              <img src={c.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name)}&background=128C7E&color=fff`} alt="" style={{ width: '22px', height: '22px', borderRadius: '50%' }} />
                              <span style={{ fontWeight: 'bold' }}>{c.name}</span>
                            </div>
                            <span style={{ fontSize: '0.65rem', color: '#8696a0' }}>{c.role?.replace('_', ' ') || 'Advocate'}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* MODAL ACTION BUTTONS */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                type="button"
                onClick={async () => {
                  if (!newStatusText.trim() && !newStatusMediaUrl && !newStatusLinkUrl) {
                    alert('Please enter status content, image/video media, or a URL link.');
                    return;
                  }

                  try {
                    const payload = {
                      mediaType: statusMediaType,
                      mediaUrl: newStatusMediaUrl || undefined,
                      linkUrl: newStatusLinkUrl || undefined,
                      content: newStatusText.trim(),
                      targetAudience: statusTargetAudience,
                      allowedUserIds: statusTargetAudience === 'specific' ? statusAllowedUserIds : []
                    };

                    const res = await api.post('/chat/status', payload);
                    if (res.data) {
                      setStatuses(prev => [res.data, ...prev]);
                      setShowStatusModal(false);
                      setNewStatusText('');
                      setNewStatusMediaUrl('');
                      setNewStatusLinkUrl('');
                      setStatusMediaPreview(null);
                      setStatusAllowedUserIds([]);
                    }
                  } catch(e) {
                    alert('Could not post status update. Please try again.');
                  }
                }} 
                style={{ flex: 1, padding: '0.85rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 4px 14px rgba(18, 140, 126, 0.4)' }}
              >
                Broadcast 24h Status Dispatch
              </button>
              <button type="button" onClick={() => setShowStatusModal(false)} style={{ flex: 1, padding: '0.85rem', backgroundColor: headerBg, color: textColor, border: `1px solid ${colors.borderThemeColor}`, borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
            </div>

          </div>
        </div>
      )}

      {/* FULL-SCREEN / MODAL STATUS VIEWER (STORY MODE WITH MULTI-MEDIA & URL LINKS) */}
      {activeStatusViewer && activeStatusViewer[activeStatusIndex] && (() => {
        const currentStatus = activeStatusViewer[activeStatusIndex];
        const mType = currentStatus.mediaType || (currentStatus.mediaUrl?.match(/\.(mp4|webm|mov)$/i) ? 'video' : currentStatus.mediaUrl ? 'image' : currentStatus.linkUrl ? 'url' : 'text');

        return (
          <div 
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 999999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: '"Outfit", system-ui, sans-serif' }}
            onMouseDown={() => setIsStatusPaused(true)}
            onMouseUp={() => setIsStatusPaused(false)}
            onTouchStart={() => setIsStatusPaused(true)}
            onTouchEnd={() => setIsStatusPaused(false)}
          >
            {/* Top Multi-Segment Progress Bars */}
            <div style={{ position: 'absolute', top: '16px', left: '16px', right: '16px', display: 'flex', gap: '6px', zIndex: 50 }}>
              {activeStatusViewer.map((_, i) => (
                <div key={i} style={{ flex: 1, height: '4px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div 
                    style={{ 
                      height: '100%', 
                      backgroundColor: '#25d366', 
                      width: i < activeStatusIndex ? '100%' : i === activeStatusIndex ? `${statusProgress}%` : '0%',
                      transition: i === activeStatusIndex ? 'width 0.1s linear' : 'none'
                    }} 
                  />
                </div>
              ))}
            </div>

            {/* Top Status Header Info */}
            <div style={{ position: 'absolute', top: '30px', left: '20px', right: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 50 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <img 
                  src={currentStatus.user?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentStatus.user?.name || 'Advocate')}&background=128C7E&color=fff`} 
                  alt="" 
                  style={{ width: '42px', height: '42px', borderRadius: '50%', border: '2px solid #25d366' }} 
                />
                <div>
                  <div style={{ fontWeight: '900', fontSize: '0.95rem', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {currentStatus.user?.name}
                    <span style={{ fontSize: '0.65rem', backgroundColor: colors.goldPrimary, color: 'black', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '800' }}>
                      {currentStatus.user?.role?.replace('_', ' ') || 'Advocate'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span>{new Date(currentStatus.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <span>•</span>
                    <span>{currentStatus.targetAudience === 'specific' ? '🔒 Specific Contacts' : '🌐 All Bar Members'}</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button 
                  onClick={() => setIsStatusPaused(!isStatusPaused)} 
                  title={isStatusPaused ? "Resume" : "Pause"}
                  style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0.4rem' }}
                >
                  {isStatusPaused ? <Play size={20} /> : <Pause size={20} />}
                </button>
                <button 
                  onClick={() => setActiveStatusViewer(null)} 
                  title="Close Status Viewer"
                  style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0.4rem' }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Left / Right Navigation Touch Controls */}
            <button 
              onClick={() => {
                if (activeStatusIndex > 0) {
                  setActiveStatusIndex(i => i - 1);
                }
              }}
              style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 40 }}
            >
              <ChevronLeft size={24} />
            </button>

            <button 
              onClick={() => {
                if (activeStatusIndex < activeStatusViewer.length - 1) {
                  setActiveStatusIndex(i => i + 1);
                } else {
                  setActiveStatusViewer(null);
                }
              }}
              style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 40 }}
            >
              <ChevronRight size={24} />
            </button>

            {/* MAIN STATUS CONTENT DISPLAY */}
            <div style={{ maxWidth: '640px', width: '100%', maxHeight: '72vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem', boxSizing: 'border-box' }}>
              
              {/* IMAGE DISPLAY */}
              {mType === 'image' && currentStatus.mediaUrl && (
                <div style={{ textAlign: 'center', width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <img src={currentStatus.mediaUrl} alt="Status" style={{ maxHeight: '60vh', maxWidth: '100%', borderRadius: '16px', objectFit: 'contain', boxShadow: '0 12px 40px rgba(0,0,0,0.8)' }} />
                </div>
              )}

              {/* VIDEO DISPLAY */}
              {mType === 'video' && currentStatus.mediaUrl && (
                <div style={{ width: '100%', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                  <video 
                    src={currentStatus.mediaUrl} 
                    autoPlay 
                    playsInline 
                    controls 
                    style={{ maxHeight: '60vh', maxWidth: '100%', borderRadius: '16px', backgroundColor: 'black', boxShadow: '0 12px 40px rgba(0,0,0,0.8)' }} 
                  />
                </div>
              )}

              {/* URL LINK CARD DISPLAY */}
              {mType === 'url' && (
                <div style={{ backgroundColor: '#0f172a', border: `2px solid ${colors.goldPrimary}`, borderRadius: '18px', padding: '2rem', textAlign: 'center', maxWidth: '480px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.8)' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: 'rgba(124, 58, 237, 0.2)', color: '#c084fc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem auto' }}>
                    <Globe size={30} />
                  </div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: colors.goldPrimary, margin: '0 0 0.5rem 0' }}>
                    Legal Citation / External Reference
                  </h3>
                  <div style={{ fontSize: '0.85rem', color: '#94a3b8', wordBreak: 'break-all', marginBottom: '1.5rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px' }}>
                    {currentStatus.linkUrl || currentStatus.mediaUrl}
                  </div>
                  <a 
                    href={currentStatus.linkUrl || currentStatus.mediaUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#7c3aed', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: '900', fontSize: '0.95rem', textDecoration: 'none', boxShadow: '0 4px 14px rgba(124, 58, 237, 0.5)' }}
                  >
                    Open Precedent / Web Link <ExternalLink size={16} />
                  </a>
                </div>
              )}

              {/* TEXT DISPATCH DISPLAY */}
              {mType === 'text' && (
                <div style={{ background: 'linear-gradient(135deg, #128c7e, #075e54)', borderRadius: '20px', padding: '2.5rem 2rem', textAlign: 'center', width: '100%', maxWidth: '520px', boxShadow: '0 20px 50px rgba(0,0,0,0.8)', border: '2px solid rgba(255,255,255,0.2)' }}>
                  <div style={{ fontSize: '1.35rem', fontWeight: '800', lineHeight: '1.6', color: '#ffffff', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                    {currentStatus.content}
                  </div>
                </div>
              )}

              {/* CAPTION OVERLAY FOR IMAGES/VIDEOS */}
              {(mType === 'image' || mType === 'video' || mType === 'url') && currentStatus.content && (
                <div style={{ marginTop: '1.25rem', backgroundColor: 'rgba(0,0,0,0.75)', padding: '0.65rem 1.25rem', borderRadius: '20px', fontSize: '0.9rem', color: '#f8fafc', textAlign: 'center', maxWidth: '540px', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  {currentStatus.content}
                </div>
              )}

            </div>

          </div>
        );
      })()}

      {/* GROUP ADMIN VIDEO CONFERENCE RESTRICTION MODAL */}
      {showGroupAdminRestrictionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: sidebarBg, border: '2px solid #ef4444', borderRadius: '16px', maxWidth: '480px', width: '100%', padding: '1.75rem', textAlign: 'center', boxShadow: '0 25px 60px rgba(0,0,0,0.8)' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto' }}>
              <Lock size={28} />
            </div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: textColor, margin: '0 0 0.5rem 0' }}>
              Group Admin Permission Required
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', lineHeight: '1.5', margin: '0 0 1.25rem 0' }}>
              Initiating an <strong>Elum Meet Video Conference</strong> for this group channel is strictly restricted to the <strong>Group Administrator</strong> or <strong>Superuser</strong>. Please request the Group Administrator to start the session.
            </p>
            <button 
              onClick={() => setShowGroupAdminRestrictionModal(false)}
              style={{ width: '100%', backgroundColor: '#128c7e', color: 'white', border: 'none', padding: '0.65rem', borderRadius: '8px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer' }}
            >
              Understood
            </button>
          </div>
        </div>
      )}

      {/* GROUP PROFILE, BIOGRAPHY & SETTINGS EDIT MODAL (CLICKABLE GROUP NAME & ICON) */}
      {showGroupProfileModal && selectedGroupForProfile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: sidebarBg, padding: '1.75rem', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.7)', border: `2px solid ${colors.goldPrimary}` }}>
            
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: `1px solid ${colors.borderThemeColor}`, paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Edit3 size={18} /> Group Profile & Chamber Details
              </h3>
              <button onClick={() => setShowGroupProfileModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            {/* Group Icon & Avatar Selector */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img 
                  src={editGroupAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(editGroupName || 'Group')}&background=128C7E&color=fff`} 
                  alt="Group Avatar Preview" 
                  style={{ width: '85px', height: '85px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${colors.goldPrimary}` }} 
                />
              </div>
              <div style={{ fontSize: '0.75rem', color: '#8696a0', marginTop: '0.35rem' }}>Group Crest / Judicial Icon Preview</div>
            </div>

            {/* Upload Custom Icon */}
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>📁 Upload Group Image from Device</label>
              <input 
                type="file" 
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (re) => {
                      if (typeof re.target?.result === 'string') {
                        setEditGroupAvatar(re.target.result);
                      }
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={{ width: '100%', padding: '0.45rem', borderRadius: '8px', border: `1px dashed ${colors.goldPrimary}`, backgroundColor: headerBg, color: textColor, fontSize: '0.8rem', cursor: 'pointer' }}
              />
            </div>

            {/* Or Pick Avatar Preset */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Or Select Judicial Chamber Crest Preset:</label>
              <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.4rem' }}>
                {GROUP_AVATAR_PRESETS.map((pUrl, idx) => (
                  <img 
                    key={idx} 
                    src={pUrl} 
                    alt={`Preset ${idx}`} 
                    onClick={() => setEditGroupAvatar(pUrl)}
                    style={{ 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: '50%', 
                      objectFit: 'cover', 
                      cursor: 'pointer',
                      border: editGroupAvatar === pUrl ? `2.5px solid ${colors.goldPrimary}` : '1.5px solid rgba(255,255,255,0.2)',
                      transform: editGroupAvatar === pUrl ? 'scale(1.15)' : 'none',
                      transition: 'transform 0.15s'
                    }} 
                  />
                ))}
              </div>
            </div>

            {/* Edit Group Name */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Group Title / Channel Name *</label>
              <input 
                type="text" 
                placeholder="E.g. Supreme Court Constitutional Bench Advocates..."
                value={editGroupName} 
                onChange={(e) => setEditGroupName(e.target.value)} 
                style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, outline: 'none', fontSize: '0.85rem', fontWeight: 'bold' }} 
              />
            </div>

            {/* Edit Group Description / Bio */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.35rem' }}>Group Biography / Practice Description</label>
              <textarea 
                placeholder="E.g. Practice communication channel for senior advocates, case law discussions, and cause list updates..."
                value={editGroupDesc} 
                onChange={(e) => setEditGroupDesc(e.target.value)} 
                rows={3}
                style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, outline: 'none', fontSize: '0.85rem', resize: 'vertical' }} 
              />
            </div>

            {/* Broadcast Mode Setting */}
            <div style={{ backgroundColor: headerBg, padding: '0.85rem 1rem', borderRadius: '10px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>📢 View-Only Broadcast Restriction</div>
                <div style={{ fontSize: '0.72rem', color: '#8696a0' }}>Only Group Administrators can post messages; other members have View-Only access.</div>
              </div>
              <input 
                type="checkbox" 
                checked={editGroupOnlyAdmins} 
                onChange={(e) => setEditGroupOnlyAdmins(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: '#128c7e', cursor: 'pointer' }} 
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button 
                onClick={handleSaveGroupProfile} 
                disabled={isSavingGroupProfile}
                style={{ flex: 1, padding: '0.75rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', opacity: isSavingGroupProfile ? 0.7 : 1 }}
              >
                {isSavingGroupProfile ? 'Saving Changes...' : 'Save Group Profile'}
              </button>
              <button 
                onClick={() => {
                  setShowGroupProfileModal(false);
                  setShowGroupManageModal(true);
                }} 
                style={{ padding: '0.75rem 1rem', backgroundColor: 'rgba(255,255,255,0.08)', color: colors.goldPrimary, border: `1px solid ${colors.goldPrimary}`, borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Manage Members
              </button>
              <button 
                onClick={() => setShowGroupProfileModal(false)} 
                style={{ padding: '0.75rem 1rem', backgroundColor: headerBg, color: textColor, border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ADD MEMBER MODAL */}
      {showAddMemberModal && selectedChat && selectedChat.type === 'group' && (() => {
        const currentGroup: Group = groups.find(g => g.id === selectedChat.id) || {
          id: selectedChat.id,
          name: selectedChat.name,
          description: selectedChat.detail,
          avatarUrl: selectedChat.avatarUrl,
          createdBy: user?.id || 'me',
          onlyAdminsCanPost: false,
          members: []
        };
        const nonMemberContacts = contacts.filter(c => !currentGroup?.members?.some(m => m.userId === c.id));

        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
            <div style={{ backgroundColor: sidebarBg, padding: '1.75rem', borderRadius: '16px', width: '100%', maxWidth: '480px', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.7)', border: `2px solid ${colors.goldPrimary}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: `1px solid ${colors.borderThemeColor}`, paddingBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <UserPlus size={18} /> Add Member to {currentGroup.name}
                </h3>
                <button onClick={() => setShowAddMemberModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: headerBg, border: `1px solid ${colors.borderThemeColor}`, borderRadius: '8px', padding: '0.4rem 0.75rem', marginBottom: '1rem' }}>
                <Search size={16} color={colors.goldPrimary} />
                <input 
                  type="text" 
                  placeholder="Search contacts by name, role, email..."
                  value={manageMemberSearch}
                  onChange={(e) => setManageMemberSearch(e.target.value)}
                  style={{ flex: 1, backgroundColor: 'transparent', border: 'none', color: textColor, outline: 'none', fontSize: '0.85rem' }}
                />
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <select 
                  value={selectedNewMemberId} 
                  onChange={(e) => setSelectedNewMemberId(e.target.value)}
                  style={{ width: '100%', padding: '0.65rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem' }}
                >
                  <option value="">Select advocate to add...</option>
                  {nonMemberContacts
                    .filter(c => {
                      if (!manageMemberSearch.trim()) return true;
                      const q = manageMemberSearch.toLowerCase();
                      return (
                        (c.name && c.name.toLowerCase().includes(q)) ||
                        (c.email && c.email.toLowerCase().includes(q)) ||
                        (c.role && c.role.toLowerCase().includes(q)) ||
                        (c.phone && c.phone.includes(q))
                      );
                    })
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.role.replace('_', ' ')}) • {c.email}</option>
                    ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button 
                  onClick={async () => {
                    await handleAddMemberToGroup();
                    setShowAddMemberModal(false);
                  }}
                  disabled={!selectedNewMemberId}
                  style={{ flex: 1, padding: '0.65rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', opacity: selectedNewMemberId ? 1 : 0.5 }}
                >
                  Add Member Now
                </button>
                <button 
                  onClick={() => setShowAddMemberModal(false)}
                  style={{ padding: '0.65rem 1rem', backgroundColor: headerBg, color: textColor, border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* GPS & COURT LOCATION PICKER MODAL */}
      {showLocationModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: sidebarBg, padding: '1.75rem', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.7)', border: `2px solid ${colors.goldPrimary}` }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: `1px solid ${colors.borderThemeColor}`, paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <MapPin size={20} /> Share Court / Chambers Location
              </h3>
              <button onClick={() => setShowLocationModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            {/* Quick Share Live GPS */}
            <div style={{ marginBottom: '1.25rem' }}>
              <button 
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        handleSendLocationMessage('📍 Live GPS Location', pos.coords.latitude, pos.coords.longitude, `Lat: ${pos.coords.latitude.toFixed(4)}, Lng: ${pos.coords.longitude.toFixed(4)}`);
                      },
                      (err) => {
                        alert('Could not access device GPS location. You can select any Supreme Court or High Court location below.');
                      }
                    );
                  } else {
                    alert('Geolocation is not supported on this browser.');
                  }
                }}
                style={{ width: '100%', backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.75rem', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', boxShadow: '0 4px 15px rgba(2,132,199,0.35)' }}
              >
                <MapPin size={18} /> Share My Current Device GPS Location
              </button>
            </div>

            {/* Preset Pakistani Courts */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 'bold', marginBottom: '0.5rem', color: colors.goldPrimary }}>Or Select Court / Judicial Chambers:</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {PAK_COURTS_LOCATIONS.map((loc, idx) => (
                  <div 
                    key={idx}
                    onClick={() => handleSendLocationMessage(loc.title, loc.lat, loc.lng, loc.address)}
                    style={{ backgroundColor: headerBg, padding: '0.65rem 0.85rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background-color 0.15s' }}
                  >
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{loc.title}</div>
                      <div style={{ fontSize: '0.72rem', color: '#8696a0' }}>{loc.address}</div>
                    </div>
                    <button style={{ backgroundColor: '#128c7e', color: 'white', border: 'none', padding: '0.3rem 0.65rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 'bold', cursor: 'pointer' }}>
                      Share
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <button 
              onClick={() => setShowLocationModal(false)}
              style={{ width: '100%', backgroundColor: headerBg, color: textColor, border: 'none', padding: '0.65rem', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Cancel
            </button>

          </div>
        </div>
      )}

      {/* JUDICIAL CITATION / STATUTE PRECEDENT MODAL */}
      {showCitationModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: sidebarBg, padding: '1.75rem', borderRadius: '16px', width: '100%', maxWidth: '480px', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.7)', border: `2px solid ${colors.goldPrimary}` }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: `1px solid ${colors.borderThemeColor}`, paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Scale size={20} /> Share Legal Citation / Case Law
              </h3>
              <button onClick={() => setShowCitationModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>Year</label>
                <input 
                  type="text" 
                  value={citationYear} 
                  onChange={(e) => setCitationYear(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem' }} 
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>Journal</label>
                <select 
                  value={citationJournal} 
                  onChange={(e) => setCitationJournal(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem' }}
                >
                  {['SCMR', 'PLD', 'CLD', 'YLR', 'MLD', 'PLC', 'PTD', 'PCRLJ', 'ALD'].map(j => (
                    <option key={j} value={j}>{j}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>Page No. *</label>
                <input 
                  type="text" 
                  placeholder="104"
                  value={citationPage} 
                  onChange={(e) => setCitationPage(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem', fontWeight: 'bold' }} 
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>Court / Jurisdiction</label>
              <input 
                type="text" 
                value={citationCourt} 
                onChange={(e) => setCitationCourt(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem' }} 
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>Case Title / Subject (Optional)</label>
              <input 
                type="text" 
                placeholder="E.g. Federation of Pakistan v. State Bank (Post-Arrest Bail)"
                value={citationTitle} 
                onChange={(e) => setCitationTitle(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: `1px solid ${colors.borderThemeColor}`, backgroundColor: headerBg, color: textColor, fontSize: '0.85rem' }} 
              />
            </div>

            {/* Live Preview */}
            <div style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)', padding: '0.75rem', borderRadius: '8px', border: `1px solid ${colors.goldPrimary}`, marginBottom: '1.25rem' }}>
              <div style={{ fontWeight: '900', color: colors.goldPrimary, fontSize: '0.88rem' }}>
                ⚖️ Preview: {citationYear} {citationJournal} {citationPage || '___'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
                {citationCourt} {citationTitle ? `• ${citationTitle}` : ''}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button 
                onClick={handleSendCitationMessage}
                disabled={!citationPage.trim()}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#128c7e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', opacity: citationPage.trim() ? 1 : 0.5 }}
              >
                Send Citation
              </button>
              <button 
                onClick={() => setShowCitationModal(false)}
                style={{ padding: '0.65rem 1rem', backgroundColor: headerBg, color: textColor, border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ADVOCATE CONTACT CARD SHARE MODAL */}
      {showContactShareModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: sidebarBg, padding: '1.75rem', borderRadius: '16px', width: '100%', maxWidth: '480px', maxHeight: '80vh', overflowY: 'auto', color: textColor, boxShadow: '0 25px 60px rgba(0,0,0,0.7)', border: `2px solid ${colors.goldPrimary}` }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: `1px solid ${colors.borderThemeColor}`, paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontWeight: '900', color: colors.goldPrimary, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <UserCheck size={20} /> Share Advocate Contact Card
              </h3>
              <button onClick={() => setShowContactShareModal(false)} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {contacts.map((c) => (
                <div 
                  key={c.id}
                  onClick={() => handleSendContactMessage(c)}
                  style={{ backgroundColor: headerBg, padding: '0.65rem 0.85rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background-color 0.15s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                    <img src={c.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name)}&background=128C7E&color=fff`} alt={c.name} style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' }} />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{c.name}</div>
                      <div style={{ fontSize: '0.72rem', color: '#8696a0' }}>{c.role?.replace('_', ' ')} • {c.email}</div>
                    </div>
                  </div>
                  <button style={{ backgroundColor: '#22c55e', color: 'black', border: 'none', padding: '0.3rem 0.65rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 'bold', cursor: 'pointer' }}>
                    Share
                  </button>
                </div>
              ))}
            </div>

            <button 
              onClick={() => setShowContactShareModal(false)}
              style={{ width: '100%', backgroundColor: headerBg, color: textColor, border: 'none', padding: '0.65rem', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Cancel
            </button>

          </div>
        </div>
      )}

      {/* ELUM ZOOM VIDEO CONFERENCE MODAL (WINDOW-IN-WINDOW PiP & RBAC RECORDING) */}
      <ElumVideoConferenceModal 
        isOpen={isZoomConferenceOpen}
        onClose={() => setIsZoomConferenceOpen(false)}
        meetingTopic={zoomMeetingTopic}
        isHost={true}
      />

      {/* 1-ON-1 PROFESSIONAL LEGAL STRATEGY WHITEBOARD (WINDOW-IN-WINDOW PiP & FULLSCREEN) */}
      <ProfessionalLegalWhiteboard 
        isOpen={is1on1WhiteboardOpen}
        onClose={() => setIs1on1WhiteboardOpen(false)}
        title={`🏛️ Strategic Legal Whiteboard — ${activeCall?.name || 'Advocate Consultation'}`}
        initialMode="pip"
      />

    </div>
  );
};

export default ChatSystem;
